{
  "ui": {
    "loadFailed": "Couldn’t load data. {{message}}",
    "retry": "Try again"
  },
  "product": {
    "name": "Wundrlst"
  },
  "features": {
    "eyebrow": "Why Wundrlst",
    "sectionTitle": "Powerful structure for a chaotic process",
    "sectionSubtitle": "Visas, documents, saved resources, and budget in one place — a calm surface to track what matters.",
    "f1Title": "Visa Requirements",
    "f1Desc": "Instant visa status for your nationality and destination. 500+ routes across 30+ nationalities and 80+ destinations.",
    "f2Title": "Task Management",
    "f2Desc": "Every step organized into one workspace — from documents to travel dates.",
    "f3Title": "Document Extraction",
    "f3Desc": "Upload your passport. We extract details automatically.",
    "f4Title": "Budget Tracking",
    "f4Desc": "Track costs, set budgets, see what's left.",
    "f5Title": "AI Assistant",
    "f5Desc": "Ask anything about your route. Get instant answers.",
    "f6Title": "Multiple Languages",
    "f6Desc": "Available in English and Portuguese. More languages coming."
  },
  "personas": {
    "title": "Who is Wundrlst for?",
    "subtitle": "From someone planning their first trip abroad to someone already organizing a permanent move.",
    "hoverLabel": "For you",
    "p1Title": "Professional on relocation",
    "p1Desc": "Moving for work abroad and need to coordinate visa, documents, and housing at the same time.",
    "p2Title": "International student",
    "p2Desc": "Studying abroad and tracking acceptance letters, student visa, and accommodation.",
    "p3Title": "Family moving together",
    "p3Desc": "A full-family move — documents for each member, school for children, a new home.",
    "p4Title": "Digital nomad",
    "p4Desc": "Working remotely and wanting structure for frequent moves between countries.",
    "p5Title": "Long-stay traveler",
    "p5Desc": "Planning longer stays and wanting clear tasks, documents, and budget in one workspace.",
    "p6Title": "Still exploring options",
    "p6Desc": "Researching destinations and visas — wanting clarity before committing."
  },
  "visa": {
    "unverifiedDisclaimer": "This information is AI-generated and may not reflect the latest requirements. Always verify with the official embassy or consulate before applying.",
    "pendingDisclaimer": "This information is AI-generated and may not reflect the latest requirements. Always verify with the official embassy or consulate before applying."
  },
  "taskCategories": {
    "visaApplication": "Visa Application",
    "documentCollection": "Document Collection",
    "flightBooking": "Flight Booking",
    "accommodation": "Accommodation",
    "rentalCar": "Rental Car",
    "appointment": "Appointment",
    "bankAccount": "Bank Account",
    "registration": "Registration",
    "movingLogistics": "Moving & Logistics",
    "general": "General"
  },
  "errors": {
    "workspaceNoAccess": "Workspace not found or you don’t have access.",
    "loadTasks": "Could not load tasks.",
    "loadTasksWithDetail": "{{message}}",
    "loadDocuments": "Could not load documents.",
    "required": "This field is required",
    "notFound": {
      "title": "Page not found",
      "description": "This URL doesn’t match any screen in the app. Check the address or go back to your dashboard.",
      "cta": "Go to dashboard"
    },
    "boundary": {
      "title": "Something went wrong while loading this section.",
      "reload": "Reload page",
      "tryAgain": "Try again"
    }
  },
  "workspaces": {
    "duplicateName": "A workspace with this name already exists. Choose a different name.",
    "duplicateNameDescription": "Each trip needs a unique name in your account.",
    "nameRequired": "Please enter a workspace name.",
    "destinationTbd": "To be determined",
    "toast": {
      "loadFailed": "Could not load workspaces.",
      "taskStatsFailed": "Workspaces loaded, but task stats could not: {{message}}",
      "itineraryStatsFailed": "Workspaces loaded, but itinerary data could not: {{message}}",
      "budgetStatsFailed": "Workspaces loaded, but budget rows could not: {{message}}",
      "created": "Workspace created.",
      "createdFromTemplate": "Workspace created from template.",
      "deleted": "Workspace deleted.",
      "statusDone": "Workspace marked as completed.",
      "statusUpdated": "Status updated.",
      "loginRequired": "You must be signed in.",
      "onlyOwnerDelete": "Only the owner can delete this workspace.",
      "onlyOwnerStatus": "Only the owner can change the status.",
      "onlyOwnerMembers": "Only the owner can add members.",
      "enterMemberUuid": "Enter the user UUID.",
      "statusColumnMissing": "The workspaces.status column is missing. Run the Supabase migration and reload the API schema (Settings → API).",
      "memberAdded": "Member added.",
      "noPermission": "Not allowed.",
      "memberLimit": "Member limit reached ({{max}} per workspace). Adjust VITE_MAX_WORKSPACE_MEMBERS or upgrade when billing is available.",
      "memberRemoved": "Member removed.",
      "templateCreateFailed": "Could not create workspace from template.",
      "membersTableMissing": "The workspace_members table is missing. Run Supabase migrations and reload the schema.",
      "membersTableMissingShort": "The workspace_members table is missing. Run migrations in supabase/migrations/ and reload the API schema.",
      "alreadyOwner": "You are already the owner of this workspace.",
      "missingCountries": "Select nationality, starting country, and destination.",
      "visaTypeRequired": "Choose a visa type for this route."
    }
  },
  "nav": {
    "dashboard": "Dashboard",
    "workspaces": "Workspaces",
    "templates": "Journeys",
    "resources": "Resources",
    "visaExplorer": "Visa Explorer",
    "settings": "Settings",
    "signOut": "Sign out",
    "blankWorkspace": "Blank workspace",
    "fromTemplate": "Guided setup",
    "loading": "Loading…"
  },
  "commandPalette": {
    "dialogTitle": "Command palette",
    "dialogDescription": "Search navigation, workspaces, and quick actions",
    "searchPlaceholder": "Search…",
    "navigation": "Navigation",
    "quickActions": "Quick actions",
    "workspaces": "Workspaces",
    "help": "Help",
    "empty": "No results found.",
    "openShortcutHint": "Open command palette (⌘K or Ctrl+K)",
    "navPanel": "Dashboard",
    "navTasks": "Tasks",
    "navDocuments": "Documents",
    "navBookings": "Bookings",
    "navPacking": "Packing",
    "navResources": "Resources",
    "navSettings": "Settings",
    "quickNewTask": "New task",
    "quickNewBooking": "New booking",
    "quickImportDoc": "Import document",
    "quickImportTask": "Import task",
    "helpDocs": "Documentation",
    "helpFeedback": "Send feedback",
    "adminNavigation": "Admin",
    "navAdminOverview": "Admin home",
    "navAdminArea": "Admin"
  },
  "profileMenu": {
    "openMenu": "Account menu",
    "fallbackName": "Account",
    "displaySection": "Display",
    "settingsSection": "Settings",
    "planSection": "Plan",
    "planFree": "Free",
    "planPro": "Pro",
    "upgrade": "Upgrade",
    "supportSection": "Support",
    "sendFeedback": "Send feedback",
    "signOut": "Sign out"
  },
  "dashboard": {
    "title": "Your Plans",
    "emptyTitle": "Create your first plan",
    "emptySubtitle": "Start planning your move, study, or trip.",
    "lastModified": "Modified",
    "tasks": "Tasks",
    "documents": "Documents",
    "loadingWorkspaces": "Loading your workspaces…",
    "workspaceCount_one": "{{count}} workspace",
    "workspaceCount_other": "{{count}} workspaces",
    "newWorkspace": "New workspace",
    "newWorkspaceDesc": "One workspace per destination or relocation process. For a pre-filled checklist, use",
    "newWorkspaceDescProvisioned": "We’ll create tasks from visa requirements for your passport × destination route when we have data for it.",
    "name": "Name",
    "destination": "Destination",
    "createWorkspace": "Create workspace",
    "creating": "Creating…",
    "tryAgain": "Try again",
    "blankWorkspace": "Blank workspace",
    "fromTemplate": "Guided setup",
    "workspaceCreateBlankDesc": "Start empty and add tasks manually",
    "workspaceCreateWizardDesc": "Answer a few questions — we'll build your workspace",
    "createFromTemplateCard": "Create workspace with visa checklist",
    "create": {
      "nationality": "Nationality (passport)",
      "nationalityPlaceholder": "Select country",
      "sameAsNationality": "Starting country is the same as nationality",
      "startingFrom": "Starting from",
      "startingFromPlaceholder": "Country of residence",
      "destinationPlaceholder": "Where are you going?",
      "tripType": "Trip type",
      "tripRelocation": "Relocation",
      "tripStudy": "Study",
      "tripTrip": "Trip",
      "tripNomad": "Digital nomad",
      "tripExploring": "Exploring",
      "hasVisaQuestion": "Do you already have a visa for this trip?",
      "hasVisaYes": "Yes",
      "hasVisaNo": "No",
      "visaTypeLabel": "Visa type",
      "noVisaRoute": "We don’t have this route in our visa database yet — you can still create the workspace.",
      "visaTypeAuto": "Selected automatically: {{type}}",
      "workspaceNameOptional": "Workspace name (optional)",
      "workspaceNamePlaceholder": "e.g. Portugal — Work visa",
      "tripNameLabel": "Trip / workspace name",
      "tripNameHint": "Optional — we’ll suggest a name from your destination if you leave this blank.",
      "reviewNameUnset": "Auto from destination",
      "reviewBudgetUnset": "Not set",
      "countryInvalid": "Please select a valid country"
    },
    "createWorkspaceStep": {
      "navAria": "Workspace creation steps",
      "shellSubtitle": "Set up your travel workspace step by step.",
      "destination": "Destination",
      "destinationTitle": "Passport, route, and trip type",
      "tripDetails": "Trip details",
      "tripDetailsTitle": "Name and budget",
      "visaTravel": "Visa & travel",
      "visaTravelTitle": "Visa for this route",
      "review": "Review",
      "reviewTitle": "Confirm and create"
    },
    "overview": {
      "stats": {
        "tasks": "Tasks",
        "documents": "Documents",
        "ariaLabel": "Workspace indicators"
      }
    },
    "dottedMap": {
      "title": "Destinations",
      "description": "Places on this account (from your plans)"
    }
  },
  "workspaceCard": {
    "noDestination": "No destination set",
    "overdue": "overdue",
    "overdue_other": "overdue",
    "tasks": "Tasks",
    "shared": "Shared",
    "open": "Open",
    "documents": "Documents",
    "settings": "Settings",
    "delete": "Delete",
    "deleteTitle": "Delete \"{{name}}\"?",
    "deleteDesc": "This action cannot be undone. The workspace and all its tasks, documents, and bookings will be permanently deleted.",
    "cancel": "Cancel",
    "deleting": "Deleting…",
    "justNow": "Just now",
    "minutesAgo": "{{count}}m ago",
    "hoursAgo": "{{count}}h ago",
    "yesterday": "Yesterday",
    "daysAgo": "{{count}} days ago",
    "weekAgo": "{{count}} week ago",
    "weeksAgo": "{{count}} weeks ago",
    "monthAgo": "{{count}} month ago",
    "monthsAgo": "{{count}} months ago",
    "budgetUsed": "{{pct}}% budget used",
    "openWorkspaceAria": "Open workspace {{name}}",
    "optionsAria": "Options for {{name}}"
  },
  "onboarding": {
    "cancel": "Exit",
    "wizardHeading": "Get started with Wundrlst",
    "stepOf": "Step {{current}} of {{total}}",
    "step1Title": "What type of trip are you planning?",
    "step1Subtitle": "This helps us show the right visa options and tasks.",
    "step1Of2": "Step 1 of 2",
    "step2Of2": "Step 2 of 2",
    "step2TripTitle": "Passport, residence, and destination",
    "step2TripSubtitle": "We use this to suggest visas, documents, and tasks. You can edit everything later.",
    "step2aTitle": "Where are you traveling from and to?",
    "step2aSubtitle": "We match your passport country and destination to visa types in our database.",
    "step2aWizardTitle": "Where from → to?",
    "step2aWizardSubtitle": "Passport country and destination",
    "step2RouteVisaWizardTitle": "Route & visa status",
    "step2RouteVisaWizardSubtitle": "Passport country, destination, and your visa situation",
    "step2aVisaPreviewStudy": "{{origin}} → {{destination}}: Student visa needed ✓ ({{count}} types in our database)",
    "step2aVisaPreviewTrip": "{{origin}} → {{destination}}: Visitor or trip visa needed ✓ ({{count}} types in our database)",
    "step2aVisaPreviewRelocation": "{{origin}} → {{destination}}: Work or residence visa needed ✓ ({{count}} types in our database)",
    "step2aVisaPreviewFamily": "{{origin}} → {{destination}}: Family-related visa needed ✓ ({{count}} types in our database)",
    "step2aVisaPreviewExploring": "{{origin}} → {{destination}}: A visa may be required ✓ ({{count}} types in our database)",
    "step2aVisaPreviewDigitalNomad": "{{origin}} → {{destination}}: Digital nomad visa needed ✓ ({{count}} types in our database)",
    "step2aNoTypesHint": "{{origin}} → {{destination}}: We don’t have visa types for this route yet — you can continue; we’ll fill in visa details when possible.",
    "noRouteData": "We don't have visa data for this route yet.",
    "notifyMe": "Notify me when it's available",
    "notifySuccess": "You're on the list — we'll email you when ready.",
    "notifyEmailPlaceholder": "your@email.com",
    "notifySubmit": "Submit",
    "step2bTitle": "Visa status",
    "step2bSubtitle": "Tell us whether you already hold a visa, plan to apply, or can enter visa-free for short visits.",
    "step2bWizardTitle": "Visa status",
    "step2bWizardSubtitle": "Travel to {{destination}} — what applies to you?",
    "step2bHaveValid": "I already have a valid visa for this trip",
    "step2bNeedApply": "I need to apply for a visa",
    "step2bHaveValidWithPreview": "I already have a valid {{visa}} visa",
    "step2bNeedApplyWithPreview": "I need to apply for a {{visa}} visa",
    "step2bVisaProductShortStudy": "student",
    "step2bVisaProductShortTrip": "visitor / trip",
    "step2bVisaProductShortRelocation": "work or residence",
    "step2bVisaProductShortFamily": "family",
    "step2bVisaProductShortDigitalNomad": "digital nomad",
    "step2bVisaProductShortGeneric": "relevant",
    "step2bVisaFree": "Visa-free or short visit (no visa required for my plan)",
    "step2bSubtypeLabel": "Visa type",
    "step2bSingleTypeHint": "Route has one type in our database: {{type}} — we’ll use it for your workspace.",
    "step2cTitle": "Trip budget",
    "step2cSubtitle": "Slide to set an estimated total budget. Currency follows your passport country.",
    "step2cWizardTitle": "Trip budget",
    "step2cWizardSubtitle": "Estimate your total trip cost",
    "step2cAmountLabel": "Estimated budget",
    "step2cCurrencyFromNationality": "Currency from passport country",
    "step2cWorkspaceCurrencyHint": "This sets the currency for this workspace only",
    "step2cCurrencySearch": "Search currency…",
    "step2cRangeHint": "Range {{min}}–{{max}} (whole numbers in your currency).",
    "step2cUnlimitedHint": "Type any amount above, or use the slider (up to {{sliderMax}}) as a quick guide — there is no maximum budget.",
    "tripPassportOrigin": "Passport country (nationality)",
    "useCurrentLocation": "Use current location",
    "locationDetecting": "Detecting…",
    "locationDenied": "Location access was denied.",
    "locationFailed": "Could not detect your country from your location.",
    "locationUnavailable": "Location is not supported in this browser.",
    "tripResidence": "Country where you live now",
    "tripDestination": "Destination country",
    "tripContextVisaHaveQuestion": "Do you already have a visa for {{destination}}?",
    "tripContextVisaHaveYes": "Yes, I have one",
    "tripContextVisaHaveNo": "No, I need one",
    "tripContextWhichVisaHave": "Which visa type do you have?",
    "tripContextWhichVisaApplying": "Which type of visa are you applying for?",
    "budgetLabel": "Estimated total budget",
    "budgetOptional": "(optional)",
    "budgetAmountPlaceholder": "0",
    "visaProceedHint": "When you’re ready, tap Continue — we’ll create your workspace next.",
    "step2Title": "What is your nationality?",
    "step2Subtitle": "This helps us identify the visas available to you.",
    "step3Title": "Where are you moving from?",
    "step3Subtitle": "Your current country of residence.",
    "sameAsNationality": "Same as my nationality",
    "step4Title": "Where do you want to go?",
    "step4Subtitle": "Choose your main destination.",
    "step5Title": "Do you already have a visa for {{destination}}?",
    "step5Subtitle": "Tell us if you already have a valid visa.",
    "hasVisa": "Yes, I already have a visa",
    "noVisa": "No, I don't have one yet",
    "whichVisa": "Which visa do you have?",
    "visaSkipNote": "✓ We won't show visa-related tasks.",
    "loadingVisaTypes": "Loading visa types…",
    "loadingVisaData": "Loading visa data…",
    "visaApiError": "We couldn't load visa requirements. Please try again.",
    "retryVisa": "Try again",
    "visaFreeMessage": "For travel with a passport issued in {{passportCountry}}, you typically do not need a visa for short visits to {{destination}} (per the data source).",
    "visaFreeConflictIntro": "You selected {{visaType}} for {{passportCountry}} → {{destination}}. The line below reflects general short-visit / tourism rules from the data source — it does not replace permits for work, long-term study, or residence.",
    "visaFreeRelocationGoalHint": "You’re planning a longer stay or move. Short-visit rules may not apply — choose the visa type that matches your situation and confirm with the official authority.",
    "visaOnArrivalMessage": "With a passport issued in {{passportCountry}}, you may be eligible for a visa on arrival in {{destination}}. Confirm eligibility, fees, and maximum stay with the embassy or an official source.",
    "visaOnArrivalConflictIntro": "You selected {{visaType}}. Visa on arrival rules, when they exist, are often limited by purpose and length of stay — confirm they cover your plan for {{destination}} before you travel.",
    "visaStatusHint": "For a {{passportCountry}} passport traveling to {{destination}}, the data source reports: {{status}}.",
    "visaBannerVisaTypeLine": "Your selected visa type: {{visaType}}.",
    "visaRequirementsVerifyNotice": "Immigration rules for travelers from {{passportCountry}} to {{destination}} change frequently. Always verify with the official authority before you travel or apply.",
    "visaRequirementsVerifyNoticeWithType": "Immigration rules for travelers from {{passportCountry}} to {{destination}}, including for {{visaType}}, change frequently. Always verify with the official authority before you travel or apply.",
    "visaTypeLabels": {
      "visitor": "Tourism / visitor",
      "tourist": "Tourism",
      "work": "Work or digital nomad",
      "student": "Study",
      "business": "Business",
      "relocation": "Residence / relocation"
    },
    "whichVisaApply": "Which visa are you planning to apply for?",
    "visaDataSource": "Data: {{source}}",
    "visaLastUpdated": "Last updated: {{date}}",
    "visaSourceCache": "cache (up to 7 days old)",
    "visaSourceLive": "live API (nickypangers / Travel Buddy in production)",
    "visaSourceCacheStale": "cache (API unavailable; data may be out of date)",
    "visaSourceDev": "demo mode",
    "visaSourceMetadata": "internal visa reference",
    "noVisaTypes": "No visas found for this combination.",
    "selectVisaStatus": "Do you already have a visa?",
    "selectVisaType": "Select visa type",
    "loadingPage": "Loading…",
    "defaultWorkspaceName": {
      "study": "My study abroad",
      "family": "Family relocation",
      "trip": "My trip",
      "exploring": "My first workspace",
      "relocation": "My relocation plan"
    },
    "step6Title": "When do you plan to travel?",
    "step6Subtitle": "This helps us prioritize your tasks.",
    "timeline3m": "Within the next 3 months",
    "timeline3mDesc": "You need to act now — we'll prioritize urgent tasks.",
    "timeline3to6m": "Between 3 and 6 months",
    "timeline3to6mDesc": "Good pace to prepare documents and research options.",
    "timeline6mplus": "More than 6 months",
    "timeline6mplusDesc": "There's time to plan at a comfortable pace.",
    "timelineUnknown": "I don't know yet",
    "timelineUnknownDesc": "No rush — organize your ideas first.",
    "step7Title": "Who is traveling?",
    "step7Subtitle": "We'll adapt the plan accordingly.",
    "whoSolo": "Just me",
    "whoSoloDesc": "A plan focused on your individual journey.",
    "whoCouple": "Me and my partner",
    "whoCoupleDesc": "Accounts for two people and shared logistics.",
    "whoFamily": "Family",
    "whoFamilyDesc": "Includes children, school, healthcare, and more.",
    "termsLabel": "I accept the",
    "termsLink": "Terms & Conditions",
    "privacyLink": "Privacy Policy",
    "andThe": "and the",
    "mustAcceptTerms": "You must accept the terms to continue.",
    "continue": "Continue",
    "skip": "Skip",
    "back": "Back",
    "clearDestination": "Clear destination",
    "completeOnboarding": "Complete onboarding",
    "creatingPlan": "Creating…",
    "chooseReadyPlan": "Choose a ready-made plan (you can edit everything later)",
    "startBlank": "Start blank",
    "country": "Country",
    "city": "City (optional)",
    "searchCountry": "Search country…",
    "searchCurrency": "Search currency…",
    "fxLoadingCurrencies": "Loading currency list…",
    "nationality": "Nationality",
    "location": "Current country of residence",
    "destination": "Destination",
    "countryPlaceholder": "e.g. Portugal, Canada, Spain…",
    "citySearchPlaceholder": "Search city…",
    "cityManualPlaceholder": "e.g. Lisbon, Toronto…",
    "chooseCityFirst": "Choose a country to see suggested cities.",
    "cityListAvailable": "City list for {{country}} — search or tap to select. You can also type another name.",
    "cityListUnavailable": "We don't have a city list for this country name — type the city manually.",
    "loadingVisa": "Loading visa information…",
    "updatingVisa": "Updating visa context…",
    "commonVisas": "Common visas (guidance)",
    "visaForBrazilians": "For Brazilians going to {{country}} as {{goal}}",
    "frequentVisas": ", frequently cited visas or processes: {{visas}}.",
    "noVisaList": " — no visa types found in this database for this combination.",
    "estimatedTimeline": "Estimated timeline:",
    "confirmWith": "Confirm with:",
    "officialSite": "official site",
    "visaDisclaimer": "This information is for guidance only. Confirm with official sources or an immigration consultant.",
    "visaContextTitle": "Visas in your scenario",
    "visaTypesListed": "Types listed in database: {{visas}}.",
    "estimatedDeadline": "Estimated deadline:",
    "noVisaEntryFound": "We didn't find a visa entry for this country and goal — you can still continue and add tasks manually.",
    "goalRelocation": "Relocation",
    "goalRelocationDesc": "Moving abroad for work, family, or long-term stay.",
    "goalStudy": "Study Abroad",
    "goalStudyDesc": "Student visa, documents, and arrival prep.",
    "goalFamily": "Family relocation",
    "goalFamilyDesc": "One plan for everyone.",
    "goalTrip": "Vacations",
    "goalTripDesc": "Bookings, checklists, and what to expect at the border.",
    "goalExploring": "Just exploring",
    "goalExploringDesc": "Organize ideas before deciding.",
    "goalDigitalNomad": "Digital Nomad",
    "goalDigitalNomadDesc": "Remote work abroad with the right visa and setup.",
    "failedToSave": "Failed to save progress — try again",
    "mustSignIn": "You must be signed in.",
    "workspaceCreated": "Workspace created.",
    "provisioningWorkspaceRoute": "Setting up your {{origin}} → {{dest}} workspace…",
    "failedToComplete": "Não foi possível concluir o onboarding.",
    "failedToSkip": "Failed to skip onboarding — try again",
    "profileNotSaved": "Workspace pronto, mas o perfil não foi salvo: {error}",
    "schemaHelp": "No Supabase: SQL → cole o conteúdo de supabase/apply_profiles_onboarding_columns.sql → Run. Depois: Settings → API → Reload schema."
  },
  "workspaceWizard": {
    "brand": "Wundrlst",
    "wizardHeading": "Create a new workspace",
    "pageTitle": "New workspace",
    "cancel": "Back to trips",
    "stepOf": "Step {{current}} of {{total}}",
    "backShort": "← Back",
    "step7Title": "Who is this plan for?",
    "step7Subtitle": "We’ll personalize tasks and hints. You can change this later.",
    "createWorkspace": "Create workspace",
    "creatingWorkspace": "Creating workspace…",
    "created": "Workspace created.",
    "createFailed": "Could not create the workspace.",
    "createBlankNow": "Create blank workspace",
    "createWorkspaceHint": "We’ll create your workspace and add visa checklist tasks when we have data for your route."
  },
  "workspace": {
    "breadcrumbHome": "Dashboard",
    "tasksProgress": "{{done}}/{{total}} tasks",
    "taskProgressComplete": "{{done}} of {{total}} tasks complete",
    "noDestination": "No destination",
    "routeInProgress": "We’re building out this route",
    "routeInProgressBody": "Step-by-step visa guidance for {{nat}} → {{dest}} is coming soon. Your tasks below are a starting point — we recommend also checking the official embassy website.",
    "checkEmbassy": "Check embassy →",
    "resourcesSuggested": "Suggested resources",
    "editWorkspaceName": "Edit workspace name",
    "saveWorkspaceName": "Save",
    "cancelRename": "Cancel",
    "workspaceNameEmpty": "Workspace name cannot be empty.",
    "workspaceRenameFailed": "Could not update the workspace name.",
    "workspaceRenamed": "Workspace name updated.",
    "error": {
      "message": "Your workspace was created, but some checklist tasks could not be generated automatically.",
      "viewAnyway": "View workspace",
      "retry": "Go to Tasks"
    },
    "headerAddTask": "Add new task",
    "header": {
      "shareButton": "Share workspace",
      "progress": "{{done}} / {{total}} tasks"
    },
    "chatbot": {
      "globalDefaultsNotice": "Global AI defaults live under Account settings → AI. These workspace settings currently override your global defaults.",
      "openGlobalSettings": "Open global AI settings"
    },
    "create": {
      "step": "Step {{current}} of {{total}}",
      "back": "← Back",
      "invalidCountry": "That country is not in the list. Pick a valid country from the list.",
      "provisionCreating": "Creating workspace…",
      "provisionSeedingVisa": "Seeding visa tasks…",
      "provisionDocuments": "Preparing documents…",
      "provisionAlmostReady": "Almost ready…"
    },
    "overview": {
      "dismissBanner": "Dismiss welcome banner"
    },
    "stickyBar": {
      "tasks": "{{done}} / {{total}} tasks",
      "budget": "{{pct}}% budget used",
      "days": "{{count}} days until trip"
    },
    "travelDate": {
      "widgetTitle": "Trip date",
      "widgetDescription": "When your workspace stores a trip date, a countdown and timeline context will appear here. (Not available yet.)"
    }
  },
  "workspaceSettings": {
    "pageTitle": "Workspace settings",
    "pageTitleWithName": "{{name}} — settings",
    "lead": "Custom task fields and collaborators. Invite people by email with a secure link.",
    "navLabel": "Workspace settings sections",
    "navGeneral": "General",
    "navChatbot": "AI assistant",
    "loading": "Loading settings…",
    "customFields": "Custom fields on tasks",
    "customFieldsDescription": "Define reusable keys for task forms and the table.",
    "onlyOwnerCanEdit": "Only the workspace owner can edit this list.",
    "loadingFields": "Loading…",
    "noExtraFields": "No extra fields yet.",
    "newField": "New field",
    "inviteCollaborator": "Invite collaborator",
    "inviteDesc": "Send an invitation by email. Share the link with them — they can create an account if they do not have one yet.",
    "email": "Email",
    "emailPlaceholder": "name@example.com",
    "inviteButton": "Invite",
    "noCollaborators": "No collaborators yet.",
    "collaborator": "Collaborator",
    "remove": "Remove",
    "fieldLabel": "Label",
    "fieldLabelExample": "e.g. Case number",
    "fieldType": "Type",
    "fieldTypeText": "Text",
    "fieldTypeNumber": "Number",
    "fieldTypeDate": "Date",
    "fieldTypeBoolean": "Yes / No",
    "fieldKey": "The technical key is generated from the label.",
    "cancel": "Cancel",
    "save": "Save",
    "noAccount": "No account found with that email. Ask them to sign up first.",
    "inviteFailed": "Could not invite collaborator. Please try again.",
    "alreadyCollaborator": "This person is already a collaborator.",
    "inviteSuccess": "Collaborator added.",
    "inviteSentTitle": "Invite link created",
    "inviteSentDescription": "Share this link with them:\n{{link}}",
    "invitePendingExists": "There is already a pending invite for this email.",
    "invitesUnavailable": "Collaborator invites are not available yet. You can still add members by user ID when that option is enabled.",
    "inviteSelf": "You cannot invite your own email.",
    "existingAccess": "People with existing access",
    "memberBadge": "member"
  },
  "acceptInvite": {
    "invalidLink": "Invalid invite link.",
    "headlineGuest": "You have been invited to collaborate on Wundrlst",
    "guestHint": "Create an account or sign in with the same email the invitation was sent to. Then you can accept the invite.",
    "createAccount": "Create account",
    "signIn": "I already have an account, sign in",
    "acceptButton": "Accept invite",
    "expiredOrInvalid": "This invite has expired or is no longer valid.",
    "backHome": "Back to home",
    "backDashboard": "Go to dashboard",
    "invitedBy": "Invited by",
    "copyright": "© {{year}} Wundrlst",
    "footerPrivacy": "Privacy",
    "footerTerms": "Terms"
  },
  "admin": {
    "overview": "Health",
    "routes": {
      "title": "Routes",
      "funnelPending": "Awaiting review",
      "funnelLive": "Live",
      "funnelInReview": "In review",
      "funnelVerified": "Verified",
      "funnelOther": "Other",
      "toolbarStatusFlagged": "Flagged for review",
      "statsNeedsReview": "Needs review",
      "toolbarLocalePlaceholder": "Content locale",
      "toolbarLocaleSearchPlaceholder": "Search locale…",
      "toolbarLocaleOptionAll": "All Locales",
      "toolbarLocaleOptionEnglish": "English",
      "toolbarLocaleOptionPortugueseBrazil": "Portuguese (Brazil)",
      "statsVerifiedNotLive": "Verified, not live",
      "statsVerifiedNotLiveHint": "Verified but not published to travellers yet — clear this queue when possible.",
      "toolbarStatusVerifiedNotLive": "Verified, not live"
    },
    "shellLabel": "Admin",
    "quality": {
      "sources": "{{count}} sources",
      "missing": "{{count}} missing",
      "none": "No linked sources"
    },
    "enrichment": {
      "revert": "Revert",
      "revertConfirm": "Restore the previously saved process steps from before the last enrichment? The database revert is not wired yet — this only acknowledges your intent.",
      "revertQueued": "Revert queued. Backend support will apply the previous version."
    },
    "settings": {
      "nav": "Settings",
      "pipeline": {
        "title": "Pipeline config",
        "description": "Controls which AI models run visa route enrichment in Edge Functions. Changes apply on the next enrichment run — no redeploy.",
        "primary": "Primary model (full enrichment)",
        "validator": "Validator model (visa status check)",
        "validatorEnabled": "Run validator after primary succeeds",
        "fallbackOrder": "Fallback order",
        "fallbackOrderHint": "If the primary model fails, models are tried in this order.",
        "save": "Save changes",
        "saving": "Saving…",
        "reload": "Reload from database",
        "loadFailed": "Could not load pipeline config. {{message}}",
        "saveSuccess": "Pipeline config saved.",
        "saveFailed": "Could not save. {{message}}",
        "loading": "Loading pipeline settings…"
      }
    },
    "openAdminPanel": "Admin panel",
    "sidebarAreaLabel": "Administration",
    "verify": {
      "drawerTitle": "Verification review",
      "keyFacts": "Key facts",
      "processSteps": "Process steps",
      "sources": "Sources",
      "totalCost": "Total cost",
      "totalTime": "Total time",
      "markVerified": "Mark as Verified",
      "skipRoute": "Skip",
      "flagIssue": "Flag Issue",
      "flagNote": "What’s wrong with this route?",
      "flagSubmit": "Submit Flag",
      "flagSuccess": "Route flagged for re-enrichment",
      "verifySuccess": "Route verified",
      "queueProgress": "{{verified}} verified this session | {{remaining}} remaining",
      "queueEmpty": "No pending routes in the review queue.",
      "reviewQueue": "Review Queue ({{count}})"
    },
    "visaMetadata": {
      "markVerifiedToolbar": "Mark as Verified",
      "markVerifiedLoading": "Verifying rows…",
      "bulkActionDisabledAllFilteredHint": "Use Mark as Verified, Flag, or Re-enrich from the toolbar for all filtered routes.",
      "enrichSelectedDisabledAllFilteredHint": "Select individual rows to enrich. Bulk enrich all is not supported to avoid overloading the enrichment pipeline.",
      "bulkDeleteDisabledAllFiltered": "Bulk delete applies to rows on this page only. Narrow filters or switch pages.",
      "bulkDeleteUnsupportedAllFiltered": "Deleting every filtered route isn’t supported from this toolbar. Narrow the selection to the current page or change filters.",
      "markVerifiedSuccess": "Marked {{count}} rows as verified",
      "markVerifiedFailed": "Failed to verify rows: {{message}}",
      "exportSelected": "Export Selected",
      "exportSuccess": "Exported {{count}} routes",
      "exportEmpty": "Nothing to export.",
      "exportFailed": "Export failed: {{message}}",
      "nav": "Routes",
      "verifyAction": "Verify",
      "verifySuccess": "Route verified",
      "verifyRowFailed": "Could not approve route: {{message}}",
      "columnConfidenceScore": "Confidence",
      "confidenceTooltip": "AI confidence in this route's data quality. Below 0.70 may benefit from re-enrichment.",
      "confidenceTooltipAria": "About AI confidence scores",
      "viewNeedsReview": "Needs review",
      "approveRoute": "Approve",
      "needsReviewFocusButton": "Needs review ({{count}})",
      "statsNeedsReviewHint": "Routes awaiting verification or flagged for follow-up (combined).",
      "quickReEnrichStarting": "Re-enrich…",
      "quickReEnrichOk": "{{count}} route queued for re-enrichment (~ {{estimate}})",
      "flagForReviewSingleOk": "Route flagged for review.",
      "flagForReviewSingleNoOp": "No changes applied.",
      "verificationFilterLabel": "Verification",
      "verificationAll": "All",
      "verificationUnverified": "Unverified",
      "verificationPendingReview": "Pending Review",
      "verificationVerified": "Verified",
      "enrichmentPipelineLegend": "Enrichment pipeline",
      "columnsToggle": "Columns",
      "columnsToggleMenu": "Visible columns",
      "columnEnrichmentStatus": "Enrichment Status",
      "columnVerificationStatus": "Verification Status",
      "columnFlagForReview": "Flagged",
      "columnFlagForReviewColumn": "Flag for review",
      "flagForReviewBadge": "Flagged",
      "columnContentLocale": "Language",
      "columnCreatedAt": "Created",
      "columnUpdatedAt": "Updated",
      "statusColumnHelpAria": "What enrichment statuses mean",
      "manualOverrideHint": "Has admin override",
      "dryRunTitle": "Dry run",
      "dryRunDescription": "Runs enrichment without saving changes to the database.",
      "flagForReviewButton": "Flag for Review",
      "flagForReviewLoading": "Flagging rows for review…",
      "flagForReviewSuccess": "Flagged {{count}} rows for review",
      "flagForReviewFailed": "Failed to flag rows: {{message}}",
      "reEnrichEstimate": "Marked {{count}} routes for re-enrichment. Estimated time: {{estimate}}.\n\nCheck back later or run 'npm run visa:enrich-pending' to start.",
      "dryRun": {
        "showRawJson": "Show raw JSON",
        "hideRawJson": "Hide raw JSON",
        "stepsFoundChip": "{{count}} steps found",
        "documentsRequiredChip": "{{count}} documents required",
        "estimatedCostChip": "{{cost}} estimated cost USD"
      },
      "status": {
        "pending": {
          "tooltip": "Route exists but not AI-enriched yet"
        },
        "enriching": {
          "tooltip": "AI enrichment running (Gemini 2.5)"
        },
        "approved": {
          "tooltip": "Admin reviewed and approved, not user-visible"
        },
        "live": {
          "tooltip": "Active — users see this in new workspaces"
        }
      },
      "pushLive": {
        "dryRunRecommendedTitle": "Dry-run recommended",
        "dryRunRecommendedBody": "You haven't run a dry-run for this route. Preview changes first.",
        "dryRunReadyTitle": "Dry-run complete",
        "dryRunReadyBody": "Dry-run complete. Ready to go live.",
        "runDryRun": "Run dry-run"
      }
    },
    "visaMetadataReview": {
      "nav": "Review Queue",
      "pageTitle": "Review Queue",
      "subtitle": "Routes awaiting verification — lowest confidence first.",
      "loading": "Loading review queue…",
      "loadFailed": "Could not load review queue. {{message}}",
      "summaryTotal": "{{count}} in queue",
      "summaryHighConfidence": "{{count}} with confidence ≥ 0.85",
      "summaryDisagreements": "{{count}} with sync notes",
      "emptyTitle": "Review queue is empty",
      "emptyDescription": "When enrichment finishes with pending review, routes appear here.",
      "openRoute": "Open route",
      "sourceUnknown": "unknown source",
      "confidenceLabel": "Confidence {{value}}",
      "processStepsToggle": "Process steps ({{count}} titles)",
      "noStepTitles": "No step titles in saved enrichment.",
      "requiredDocuments": "Required documents",
      "noDocuments": "No documents listed.",
      "approve": "Approve",
      "reEnrich": "Re-enrich",
      "approveSuccess": "Route approved and published (live).",
      "approveFailed": "Could not approve route. {{message}}",
      "approveLiveGateDbFix": "Approve blocked by the database: the old trigger overwrites verification when going live. Apply pending Supabase migrations (e.g. supabase db push), or run the SQL in supabase/migrations/20260506210000_reapply_visa_metadata_on_enrich_noop.sql in the Supabase SQL Editor.",
      "reEnrichSuccess": "Route reset for re-enrichment.",
      "reEnrichFailed": "Could not reset route. {{message}}",
      "filterNationality": "Nationality",
      "filterAllNationalities": "All nationalities",
      "filterConfidence": "Confidence",
      "confidenceAll": "All scores",
      "confidenceHigh": "≥ 0.85",
      "confidenceMid": "0.50 – 0.84",
      "confidenceLow": "< 0.50",
      "filterSyncNote": "Sync note",
      "syncNoteAll": "Any",
      "syncNoteYes": "Has sync note",
      "syncNoteNo": "No sync note",
      "selectAllFiltered": "Select all matching filters ({{count}})",
      "filteredCount": "{{count}} routes match current filters.",
      "filterEmptyTitle": "No routes match filters",
      "filterEmptyDescription": "Adjust filters or clear nationality to see the queue again.",
      "clearFilters": "Clear filters",
      "batchToolbarLabel": "Batch actions for selected routes",
      "selectedCount": "{{count}} selected",
      "batchApprove": "Approve selected ({{count}})",
      "batchReenrich": "Re-enrich selected ({{count}})",
      "batchApproveProgress": "Approving… {{current}} / {{total}}",
      "batchReenrichProgress": "Resetting… {{current}} / {{total}}",
      "batchApproveSummary": "Approved {{ok}} routes. {{failed}} failed.",
      "batchReenrichSummary": "Reset {{ok}} routes for re-enrichment. {{failed}} failed.",
      "batchApproveFailed": "Batch approve failed. {{message}}",
      "batchReenrichFailed": "Batch re-enrich failed. {{message}}",
      "selectPageAria": "Select all rows on this page",
      "selectRowAria": "Select route {{route}}",
      "colRoute": "Route",
      "colConfidence": "Confidence",
      "colSync": "Sync note",
      "colActions": "Actions",
      "pageStatus": "Page {{page}} of {{pages}} · {{showing}} of {{total}} routes",
      "prevPage": "Previous",
      "nextPage": "Next"
    },
    "routeDetail": {
      "routeSaved": "Route saved",
      "unsavedChanges": "Unsaved changes",
      "hasOverrides": "Has overrides",
      "loading": "Loading route…",
      "requirementNote": "Requirements note",
      "goalTypeField": "Goal type",
      "contentLocaleField": "Content locale",
      "requiredDocsSection": "Required documents",
      "addDocument": "Add document",
      "addBullet": "Add items",
      "bulletPlaceholder": "One bullet per line",
      "titlePt": "Title (pt-BR)",
      "descriptionPt": "Description (pt-BR)",
      "readMode": "Read mode",
      "editMode": "Edit",
      "backToTable": "Back to table",
      "notFound": "Route not found.",
      "adminNotes": "Admin notes",
      "overrideActive": "Override active",
      "noOverride": "No override",
      "showingOverride": "Showing admin override",
      "overrideSaved": "Override saved — users will see this instead of AI output",
      "clearOverrides": "Clear all overrides",
      "confirmClearOverrides": "Clear all overrides?",
      "confirmClearOverridesDescription": "This will remove every manual override for this route.",
      "keyFacts": "Key facts",
      "visaStatus": "Visa status",
      "maxStay": "Max stay",
      "costUsd": "Cost USD",
      "processingTime": "Processing time",
      "documents": "Documents",
      "contentLocale": "Content locale: {{locale}}",
      "lastEnriched": "Last enriched: {{ts}}",
      "goalType": "Goal type: {{goal}}",
      "processSteps": "Process steps",
      "noProcessSteps": "No enriched process steps found.",
      "where": "Where:",
      "documentsNeeded": "Documents needed:",
      "commonMistakes": "Common mistakes:",
      "links": "Links:",
      "notes": "Notes:",
      "sources": "Sources",
      "totals": "Totals",
      "totalCost": "Total cost",
      "totalTime": "Total time",
      "commonPitfalls": "Common pitfalls",
      "successTips": "Success tips",
      "overrideFields": "Override fields",
      "useOverride": "Use override",
      "useAiOutput": "Use AI output",
      "costOverride": "Cost override",
      "timelineOverride": "Timeline override",
      "documentsOverride": "Documents override",
      "tipsOverride": "Tips override",
      "pitfallsOverride": "Pitfalls override",
      "stepsOverride": "Steps override",
      "addStep": "+ Add step",
      "listPlaceholder": "One item per line",
      "save": "Save",
      "cancel": "Cancel"
    },
    "health": {
      "title": "Health",
      "kpiTotalRoutes": "Total routes",
      "kpiTotalRoutesHint": "All rows in visa_metadata",
      "kpiLiveVerified": "Live & verified",
      "kpiLiveVerifiedHint": "{{pct}}% of catalog",
      "kpiPendingEnrichment": "Pending enrichment",
      "kpiPendingEnrichmentHint": "Awaiting AI pipeline",
      "kpiPendingReview": "Pending review",
      "kpiPendingReviewHint": "Queue or flagged for follow-up",
      "coverageOverviewHint": "Share of all routes by enrichment status",
      "dataQualityHint": "Quality metrics for live (user-visible) routes",
      "dqVerifiedShare": "Verified share of live routes",
      "dqAvgConfidence": "Avg. confidence (live)",
      "dqLowConfidence": "Below 0.70 confidence",
      "dqReviewLowConfidence": "Review low-confidence routes",
      "sectionApiHealth": "API health",
      "apiHealthHint": "Live routes by LLM source, with activity in the last 7 days",
      "apiSource": {
        "gemini": "Google Gemini",
        "deepseek": "DeepSeek",
        "mistral": "Mistral",
        "openrouter": "OpenRouter",
        "unknown": "Unknown"
      },
      "apiSourceRouteCount": "{{count}} routes",
      "apiActivityOk": "OK",
      "apiActivityStale": "No enrichments (7d)",
      "sectionMostUsed": "Most-used routes",
      "mostUsedHint": "Workspaces grouped by destination and passport country",
      "mostUsedEmpty": "Not enough workspace data yet",
      "mostUsedLine": "{{origin}} → {{dest}} — {{count}} workspaces",
      "externalTools": "External tools",
      "pendingActions": "{{review}} routes pending review · {{enrich}} routes pending enrichment",
      "reviewRoutesCta": "Review routes →",
      "enrichRoutesCta": "Enrich routes →",
      "exchangeRates": "Exchange Rates",
      "visaData": "Visa Data Coverage",
      "enrichmentActivity": "Recent Enrichment Activity",
      "verification": "Verification Progress",
      "ratesAsOf": "Rates as of {{date}}",
      "updatedDaysAgo": "Updated {{days}} days ago",
      "staleRates": "Rates may be outdated — finances and currency displays may not be accurate",
      "refreshRates": "Refresh Rates Now",
      "refreshSuccess": "Exchange rates refreshed.",
      "refreshFailed": "Could not refresh rates. {{message}}",
      "lastChecked": "Last checked: {{ts}}",
      "goToQueue": "Go to enrichment queue",
      "coverageLine": "Total routes: {{total}} | Live: {{live}} | Pending enrich: {{pending}}",
      "verificationLine": "Verification: {{verified}} verified | {{pending}} pending review | {{unverified}} unverified",
      "verifiedPct": "{{pct}}% of live routes verified",
      "last24hLine": "Last 24h: {{enriched}} enriched, {{failed}} failed",
      "failuresBadge": "{{count}} failures — check logs",
      "viewEnrichmentQueue": "View enrichment queue",
      "verificationProgressLine": "{{verified}} of {{live}} live routes verified ({{pct}}%)",
      "pendingReviewLine": "{{count}} routes pending your review",
      "goToVerificationQueue": "Go to verification queue",
      "noIssues": "All systems look healthy.",
      "exchangeStatusOk": "OK",
      "exchangeStatusWarning": "Warning",
      "exchangeStatusError": "Error",
      "activityHealthy": "OK",
      "externalToolsHint": "Quick links for ops workflows.",
      "sectionRouteCoverage": "Route coverage",
      "sectionDataQuality": "Data quality",
      "sectionSystemStatus": "System status",
      "routeCoverageEmpty": "No visa metadata routes yet.",
      "routeCoverageRow": {
        "live": "Live (enriched)",
        "pending": "Pending enrichment",
        "failed": "Rejected / failed enrichment"
      },
      "verifiedLiveLabel": "Verified share of live routes",
      "verifiedPctCaption": "Progress toward all live routes being verified.",
      "verificationDist": {
        "verified": "Verified",
        "pendingReview": "Pending review",
        "unverified": "Unverified / not set"
      },
      "last24hEnrichedLabel": "Enriched (last 24h)",
      "last24hFailedLabel": "Failed (last 24h)",
      "otherServices": "Other health checks",
      "emDash": "—",
      "loadError": "Could not load health.",
      "notAllowed": "Not allowed.",
      "toolSupabase": "Supabase Dashboard",
      "toolVercel": "Vercel Dashboard",
      "toolSanity": "Sanity Studio",
      "toolGitHub": "GitHub repository"
    },
    "visaMetadataImport": "Visa CSV import",
    "visaMetadataEnrich": "Visa steps review",
    "airlines": "Airlines",
    "blog": "Blog",
    "blogDescription": "Manage blog posts in Sanity Studio",
    "users": {
      "nav": "Users",
      "emptyTitle": "User management",
      "emptyDescription": "Listing accounts, roles, and invitations from the admin area is not available yet."
    },
    "resources": {
      "nav": "Resources",
      "title": "Resources",
      "description": "Default links merged into workspaces based on destination.",
      "addResource": "Add resource",
      "columnTitle": "Title",
      "columnUrl": "URL",
      "columnCategory": "Category",
      "columnDestination": "Destination",
      "columnNationality": "Nationality",
      "columnActive": "Active",
      "deleteConfirm": "Delete this resource? This cannot be undone.",
      "saveSuccess": "Resource saved",
      "deleteSuccess": "Resource deleted",
      "searchPlaceholder": "Search by title or URL…",
      "filterDestination": "Destination",
      "filterDestinationAny": "All destinations",
      "filterNationality": "Nationality",
      "filterNationalityAny": "Any nationality",
      "filterNationalityAll": "All nationalities (unscoped)",
      "filterCategory": "Category",
      "filterCategoryAll": "All categories",
      "filterDestinationSearch": "Search destinations…",
      "filterNationalitySearch": "Search nationalities…",
      "filterCategorySearch": "Search categories…",
      "statsCardShowAll": "Show all resources — reset filters",
      "statsCardFilterImmigration": "Filter table by immigration category",
      "statsCardFilterEmbassy": "Filter table by embassy category",
      "statsCardFilterVisa": "Filter table by visa category",
      "statsCardFilterGeneral": "Filter table by general category",
      "statsCardFilterActive": "Toggle active resources only",
      "activeOnly": "Active only",
      "codesLoading": "Loading filter options…",
      "statsAria": "Resource statistics",
      "statsTotal": "Total resources",
      "statsImmigration": "Immigration",
      "statsEmbassy": "Embassy",
      "statsVisa": "Visa",
      "statsGeneral": "General",
      "statsActive": "Active",
      "showingPaged": "Showing {{shown}} of {{total}} resources",
      "noRecords": "No resources found.",
      "noMatch": "No resources match your search.",
      "emptyDescription": "Try adjusting filters or add a resource.",
      "allDestinations": "All",
      "allNationalities": "All",
      "deleteTitle": "Delete resource",
      "edit": "Edit resource",
      "delete": "Delete resource",
      "category": {
        "immigration": "Immigration",
        "embassy": "Embassy",
        "visa": "Visa",
        "general": "General"
      },
      "form": {
        "addTitle": "Add resource",
        "editTitle": "Edit resource",
        "desc": "These templates are merged into new workspaces when they match destination and nationality.",
        "titleRequired": "Title is required.",
        "urlRequired": "URL is required.",
        "urlInvalid": "Enter a valid http(s) URL.",
        "description": "Description",
        "active": "Active"
      }
    },
    "checkingAccess": "Checking access…",
    "redirecting": "Redirecting…",
    "logout": "Log out",
    "redirectFailed": "Redirect failed",
    "openingAdmin": "Opening admin…",
    "goToVisaMetadata": "Go to Visa Metadata",
    "usersComingSoon": "Coming soon — admin user management is not implemented yet.",
    "resourcesComingSoon": "Coming soon — admin resources tools are not implemented yet.",
    "waitlist": {
      "nav": "Route Waitlist",
      "title": "Route Waitlist",
      "empty": "No route requests yet.",
      "loading": "Loading waitlist…",
      "columns": {
        "email": "Email",
        "route": "Route",
        "signedUp": "Signed Up"
      }
    },
    "testimonials": {
      "nav": "Testimonials",
      "title": "Testimonials",
      "banner": "Manage testimonials in Sanity Studio. Click \"Open Studio\" to add, edit, or remove entries.",
      "openStudio": "Open Sanity Studio",
      "loading": "Loading testimonials…",
      "empty": "No testimonials found in the dataset.",
      "colName": "Name",
      "colRole": "Role",
      "colCompany": "Company / country",
      "colRating": "Rating",
      "colFeatured": "On landing"
    }
  },
  "warnings": {
    "staleRates": "Currency rates from {{date}} — amounts may have changed.",
    "unverifiedVisa": "AI-generated — verify requirements with the official embassy or consulate before applying.",
    "pendingEnrichment": "We're still building out the step-by-step guide for this route. Check back soon."
  },
  "airlinesAdmin": {
    "couldNotLoad": "Could not load airlines.",
    "updated": "Airline updated.",
    "created": "Airline created.",
    "deleted": "Airline deleted.",
    "title": "Airlines",
    "description": "Manage the airline list used in flight booking tasks.",
    "addNew": "Add New",
    "searchLabel": "Search",
    "searchPlaceholder": "Search by name or code",
    "loading": "Loading airlines…",
    "noRecords": "No airlines found.",
    "noMatch": "No airlines match your search.",
    "columns": {
      "name": "Name",
      "iata": "IATA",
      "icao": "ICAO",
      "country": "Country",
      "active": "Active",
      "sort": "Sort",
      "actions": "Actions"
    },
    "edit": "Edit",
    "delete": "Delete",
    "form": {
      "editTitle": "Edit airline",
      "addTitle": "Add airline",
      "editDesc": "Update this airline and save.",
      "addDesc": "Create a new airline entry.",
      "name": "Name",
      "iata": "IATA code",
      "icao": "ICAO code",
      "country": "Country",
      "sort": "Sort order",
      "active": "Active",
      "nameRequired": "Name is required.",
      "saveFailed": "Save failed"
    },
    "deleteDialog": {
      "title": "Delete airline?",
      "description": "Are you sure you want to delete:",
      "failed": "Delete failed"
    }
  },
  "visaMetadata": {
    "couldNotLoad": "Could not load visa metadata.",
    "updated": "Visa metadata updated.",
    "created": "Visa metadata created.",
    "deleted": "Visa metadata deleted.",
    "title": "Visa metadata",
    "description": "Manage reference rows used by visa checks and onboarding.",
    "enrichmentStatsAria": "Enrichment counts across all visa metadata rows",
    "addNew": "Add New",
    "addMenu": "Add",
    "addManually": "Add manually",
    "importCsv": "Import CSV",
    "searchPlaceholder": "Search routes",
    "searchExample": "e.g. BR, Portugal, work, tourism",
    "searchHint": "Matches nationality, destination, country name, visa type, or goal.",
    "searchHintServer": "Search runs on the server (debounced). Matches nationality, destination, country label, visa type, or goal.",
    "filters": {
      "country": "Country",
      "countryPlaceholder": "Destination country",
      "continent": "Continent",
      "continentAll": "All continents",
      "regionalOrg": "Regional org",
      "orgsAll": "All orgs",
      "visaType": "Visa type",
      "visaTypesAll": "All visa types",
      "visaStatus": "Visa status",
      "statusAll": "All statuses",
      "clear": "Clear filters"
    },
    "summaryTotal": "{{count}} matching row(s).",
    "showingRange": "Rows {{start}}–{{end}} of {{total}}.",
    "rowsPerPage": "Rows per page",
    "rowsPerPageAll": "All",
    "pageAllRows": "All rows",
    "pageOf": "Page {{current}} / {{total}}",
    "prevPage": "Previous",
    "nextPage": "Next",
    "loading": "Loading visa metadata…",
    "noRecords": "No records found.",
    "noMatch": "No records match your search.",
    "selectAll": {
      "filteredBanner": "All {{count}} routes matching your filters are selected."
    },
    "columns": {
      "selectRow": "Select row",
      "selectCycleStartAria": "Select all routes on this page",
      "selectPartialAria": "Partial selection on this page. Click to select all on this page.",
      "selectPageThenAllAria": "All routes on this page selected. Click to select every matching route ({{count}}).",
      "selectFilteredAllAria": "All {{count}} routes matching filters selected. Click to clear.",
      "route": "From → To",
      "nationality": "Nationality",
      "destination": "Destination",
      "country": "Country",
      "visaType": "Visa type",
      "visaStatus": "Visa status",
      "status": "Status",
      "enrichment": "Enrichment",
      "source": "Source",
      "sources": "Sources",
      "enriched": "Enriched",
      "nationalityCurrency": "Nat. currency",
      "verification": "Verification",
      "contentLocale": "Locale",
      "createdAt": "Created",
      "updatedAt": "Updated",
      "actions": "Actions",
      "rowActionsAria": "Row actions"
    },
    "filterContinentLabel": "Continent",
    "filterContinentAll": "All continents",
    "enrichmentFilterLabel": "Enrichment",
    "enrichmentFilterAll": "All",
    "enrichmentFilterLive": "Live",
    "enrichmentFilterPending": "Pending",
    "enrichmentFilterRejected": "Rejected",
    "enrichmentFilterNotEnriched": "Not enriched",
    "enrichmentStatus": {
      "none": "None",
      "live": "Live",
      "pending": "Pending",
      "rejected": "Rejected",
      "approved": "Approved",
      "enriching": "Enriching"
    },
    "enrichedToday": "Today {{time}}",
    "enrichedDaysAgo_one": "1 day ago",
    "enrichedDaysAgo_other": "{{count}} days ago",
    "batchEnrich": "Batch enrich",
    "batchEnrichTitle": "Batch enrichment",
    "batchEnrichPhaseFetch": "Selecting up to 10 pending routes with empty steps…",
    "batchEnrichPhaseEnrich": "Calling the enrichment service (this can take a minute)…",
    "batchEnrichPhaseDone": "Finishing up…",
    "batchEnrichWorking": "Working…",
    "batchEnrichNoSession": "You must be signed in to run enrichment.",
    "batchEnrichNoRoutes": "No pending routes with empty steps to enrich right now.",
    "batchEnrichFailed": "Batch enrichment failed.",
    "batchEnrichBadResponse": "Unexpected response from the enrichment service.",
    "batchEnrichToast": "{{succeeded}} succeeded, {{failed}} failed.",
    "enrichSelectedNotLoaded": "Could not load the selected rows for enrichment.",
    "enrichSelectedTitle": "Enrich {{count}} routes",
    "enrichSelectedTitleDone": "Enrichment finished",
    "enrichSelectedDescription": "This will trigger AI enrichment for the selected rows. Processing may take a few minutes.",
    "enrichSelectedPhaseFetch": "Loading selected rows…",
    "enrichSelectedPhaseEnrich": "Running AI enrichment (this may take several minutes)…",
    "enrichSelectedSummary": "{{succeeded}} succeeded, {{failed}} failed.",
    "enrichSelectedRun": "Run enrichment",
    "enrichSelectedNoRouteDetails": "Per-route results were not included in the response; check the summary above.",
    "enrichSelectedErrorHint": "Enrichment could not finish. See details below.",
    "bulkEnrichAllPublish": "Enrich all & publish",
    "bulkEnrichAllTitle": "Enrich all routes and publish live",
    "bulkEnrichAllDescription": "All routes with empty process steps are split across two AI providers (Mistral and OpenRouter), enriched, upserted via import, and set to live. This can take a long time and uses API quota.",
    "bulkEnrichAllProgress": "{{completed}} / {{total}} routes enriched",
    "bulkEnrichAllWorking": "Running two concurrent batches…",
    "bulkEnrichAllAbort": "Stop after current route",
    "bulkEnrichAllClose": "Close",
    "pushToLive": "Push to live",
    "batchPushToLive": "Batch push to live",
    "pushedToLive": "Route pushed to live.",
    "batchPushedToLive": "{{count}} route(s) pushed to live.",
    "pushLiveDialog": {
      "singleTitle": "Push to live?",
      "singleConfirm": "Push this route to live? Users will see these steps.",
      "batchTitle": "Batch push to live?",
      "batchConfirm": "Push {{count}} approved route(s) to live? Users will see these steps.",
      "cancel": "Cancel",
      "confirm": "Push to live",
      "failed": "Could not push to live."
    },
    "edit": "Edit",
    "delete": "Delete",
    "form": {
      "editTitle": "Edit visa metadata",
      "addTitle": "Add visa metadata",
      "editDesc": "Update this row and save.",
      "addDesc": "Fill in the fields and create a new row.",
      "nationalityCode": "Nationality code",
      "nationalityError": "Nationality code must be exactly 2 letters.",
      "destinationCode": "Destination code",
      "destinationError": "Destination code must be exactly 2 letters.",
      "destinationCurrency": "Nationality currency (ISO 4217, optional)",
      "destinationCurrencyHint": "Default budget currency when we seed visa tasks for this route (after the traveler's profile currency, before USD). Often the traveler's primary currency (e.g. EUR). Leave empty if unknown.",
      "destinationCurrencyError": "Use a 3-letter ISO currency code or leave empty.",
      "country": "Country (display)",
      "goalType": "Goal type",
      "goalRequired": "Goal is required.",
      "visaType": "Visa type",
      "visaTypeRequired": "Visa type is required.",
      "requiredDocs": "Required documents",
      "noDocuments": "No documents listed.",
      "documentName": "Document name",
      "removeDocument": "Remove document",
      "visaStatus": "Visa status",
      "maxStay": "Max stay (days, optional)",
      "cost": "Cost USD (optional)",
      "processingTime": "Processing time (optional)",
      "processingExample": "e.g. 2–4 weeks",
      "note": "Note (optional)",
      "save": "Save",
      "saveFailed": "Save failed",
      "duplicateRoute": "A row for this nationality, destination, and visa type already exists.",
      "enrichmentSectionTitle": "AI enrichment",
      "enrichmentReadOnlyHint": "Read-only. Regenerate steps with batch enrichment or import.",
      "enrichmentFieldStatus": "Enrichment status",
      "enrichmentFieldSource": "Model / source",
      "enrichmentFieldEnrichedAt": "Last enriched",
      "enrichmentNoPayload": "No process steps stored for this route yet.",
      "enrichmentNoSteps": "Process steps are empty. Run enrichment to generate AI steps.",
      "enrichmentUnparsedHint": "Stored payload could not be normalized into steps. Use raw JSON below if needed.",
      "enrichmentShowRaw": "Show raw JSON",
      "enrichmentHideRaw": "Hide raw JSON",
      "enrichmentStepsTitle": "Process steps",
      "enrichmentSourcesTitle": "Official sources",
      "enrichmentRoutingNotes": "Routing notes",
      "enrichmentDelivery": "Passport delivery",
      "enrichmentTotals": "Totals",
      "enrichmentTotalCost": "Estimated total (USD)",
      "enrichmentTotalDays": "Total time estimate",
      "enrichmentPitfalls": "Common pitfalls",
      "enrichmentTips": "Success tips",
      "enrichmentLinkNotFound": "not found",
      "enrichmentStepWhere": "Where",
      "enrichmentStepDocuments": "Documents",
      "enrichmentStepLinks": "Links",
      "enrichmentStepCost": "Step cost (USD)",
      "enrichmentStepDays": "Estimated days",
      "enrichmentStepNotes": "Notes",
      "enrichmentStepMistakes": "Common mistakes",
      "enrichmentCityRules": "City rules (route)",
      "enrichmentStepCityRules": "City rules (step)",
      "enrichmentStepAppliesTo": "Applies to",
      "enrichmentStepRestrictions": "Restrictions"
    },
    "deleteDialog": {
      "title": "Delete visa metadata?",
      "confirm": "Are you sure you want to delete {record}? This cannot be undone.",
      "cancel": "Cancel",
      "delete": "Delete",
      "deleteFailed": "Delete failed"
    },
    "rowMenu": {
      "viewEdit": "View / Edit",
      "openDetail": "Open detail page",
      "enrich": "Enrich",
      "reEnrich": "Re-enrich",
      "dryRun": "Dry run",
      "promote": "Promote",
      "demote": "Demote",
      "demoteHint": "Sets enrichment back to pending. You can promote again later.",
      "verify": "Verify",
      "deleteConfirmHint": "Opens a confirmation dialog. Deleted routes cannot be restored.",
      "delete": "Delete route"
    },
    "reviewEnrichment": "Review AI steps"
  },
  "visaMetadataEnrich": {
    "title": "Visa steps — review before publish",
    "subtitle": "Check AI-generated application steps, push them live when ready, or run enrichment again for a route.",
    "backToList": "Back to visa metadata",
    "loadingList": "Loading routes…",
    "statsError": "Could not load statistics.",
    "listError": "Could not load routes.",
    "statTotal": "Total routes",
    "statTotalHint": "All rows in visa metadata.",
    "statPending": "Pending review",
    "statPendingHint": "In the queue before publish.",
    "statApproved": "Approved",
    "statApprovedHint": "Ready for the next publish step.",
    "statLive": "Live",
    "statLiveHint": "Published to users.",
    "pendingHeading": "Routes waiting for review",
    "pendingHint": "Use Preview to read the steps, Push to live when users should see them, or Reject to clear bad output. You can run Enrich again to regenerate steps.",
    "noPending": "No routes are pending or approved for review right now. When new rows are enriched, they will appear here.",
    "colRoute": "Route",
    "colStatus": "Status",
    "badgeEnriched": "AI steps ready",
    "colActions": "Actions",
    "preview": "Preview",
    "reject": "Reject",
    "enrichOne": "Enrich this route",
    "rejectedToast": "Route rejected and steps cleared.",
    "rejectFailed": "Reject failed.",
    "previewTitle": "Application steps preview",
    "previewEmpty": "There are no structured steps saved yet for this route. Run “Enrich this route” first.",
    "totalCost": "Total estimated cost",
    "totalTime": "Total estimated time",
    "stepLabel": "Step {{n}}",
    "cost": "Cost (USD)",
    "time": "Time",
    "documents": "Documents",
    "close": "Close",
    "geminiTitle": "Run AI enrichment?",
    "geminiBody": "We will call the secure enrichment service (Google Gemini first, with an automatic backup if needed). This updates the database for this route when it succeeds.",
    "geminiYes": "Yes, run enrichment",
    "geminiNo": "Cancel",
    "geminiRunning": "Calling the AI… this can take up to a minute.",
    "enrichSuccessDetail": "Done. {{note}}",
    "enrichFailed": "Enrichment failed.",
    "enrichNoResult": "No result returned for this route.",
    "batchButton": "Enrich next 10 pending routes",
    "batchNoPending": "There are no pending routes to enrich.",
    "batchTitle": "Batch enrichment",
    "batchRoutesCount": "Up to {{count}} routes in this batch.",
    "batchIntro1": "Step 1 runs a dry run: the AI generates steps but nothing is saved. Review the outcome, then you can save only successful routes.",
    "batchIntro2": "Tip: fix bad source data in the main visa metadata table before re-running enrichment.",
    "runDryRun": "Run dry run (no save)",
    "cancel": "Cancel",
    "batchProgressDry": "Dry run in progress — nothing is being written to the database.",
    "batchProgressWrite": "Saving approved AI output to the database…",
    "batchDrySummary": "Dry run finished: {{ok}} succeeded, {{fail}} failed.",
    "dryRunDone": "Dry run complete: {{ok}} ok, {{fail}} failed.",
    "dryRunFailed": "Dry run failed.",
    "batchWriteSection": "Database write",
    "batchWriteSummary": "Write finished: {{ok}} saved, {{fail}} failed.",
    "writeDone": "Saved: {{ok}} ok, {{fail}} failed.",
    "writeFailed": "Save failed.",
    "batchPayloadError": "One or more routes are missing valid requirement data for the AI.",
    "batchNothingToWrite": "No routes succeeded in the dry run, so there is nothing to save.",
    "confirmWrite": "Save {{count}} route(s)",
    "writeConfirmTitle": "Save to the database?",
    "writeConfirmBody": "You are about to write AI-generated steps for {{count}} route(s). This replaces stored steps for those routes. Continue?",
    "writeConfirmAction": "Yes, save now"
  },
  "visaMetadataImport": {
    "title": "Visa metadata — CSV import",
    "description": "Upload a CSV, validate rows, preview, then bulk upsert into Supabase via an Edge Function.",
    "downloadTemplate": "Download CSV template",
    "reset": "Reset",
    "uploadTitle": "Upload .csv file",
    "uploadHint": "Required columns: nationality_code, destination_code, visa_type, country, goal_type, required_documents (JSON array), visa_status, cost_usd, max_stay, processing_time, note.",
    "noFile": "No file selected",
    "rowsParsed": "Rows parsed",
    "rowsValid": "Valid rows",
    "rowsInvalid": "Validation errors",
    "validationErrors": "Validation errors",
    "validationHint": "Fix the CSV and re-upload. We validate enums and types to avoid bad data in `visa_metadata`.",
    "colRow": "Row",
    "colField": "Field",
    "colMessage": "Message",
    "truncatedErrors": "+{{count}} more error(s) not shown",
    "previewOk": "Preview looks good. Ready to import.",
    "previewWithErrors": "Some rows have validation errors. Only valid rows will be imported.",
    "saveEdits": "Save edits",
    "editsSaved": "Edits saved",
    "savedOk": "Edits saved. All rows validate.",
    "savedWithErrors": "Edits saved, but {{count}} validation error(s) remain.",
    "functionErrorsTitle": "Import errors returned by server",
    "functionErrorsHint": "These rows were rejected by the Edge Function. Fix the values above and try importing again.",
    "importButton": "Import {{count}} row(s)",
    "nothingToImport": "Nothing to import (0 valid rows).",
    "importSuccess": "Import complete: {{inserted}} inserted, {{updated}} updated.",
    "importPartial": "Import complete with issues: {{inserted}} inserted, {{updated}} updated, {{failed}} failed.",
    "importFailed": "Import failed.",
    "signInRequired": "Sign in again to run the import.",
    "functionUnreachable": "Could not reach the import service (network/CORS/ad blocker). Deploy `visa-import` to this Supabase project, or add your app URL to ALLOWED_ORIGINS for that function.",
    "functionNotDeployed": "visa-import Edge Function was not found. Run: supabase functions deploy visa-import",
    "truncatedPreview": "+{{count}} more row(s) not shown in preview"
  },
  "hooks": {
    "bookingAdded": "Booking added.",
    "bookingRemoved": "Booking removed.",
    "noAccess": "Workspace not found or you don't have access.",
    "fieldAdded": "Field added.",
    "fieldRemoved": "Field removed.",
    "onlyOwnerCanCreate": "Only the workspace owner can create custom fields.",
    "onlyOwnerCanRemove": "Only the workspace owner can remove fields.",
    "resourceAdded": "Resource added.",
    "resourceRemoved": "Resource removed.",
    "eventAdded": "Event added.",
    "eventRemoved": "Event removed.",
    "itemAdded": "Item added.",
    "itemRemoved": "Item removed.",
    "addedToTimeline": "Added to timeline.",
    "feedbackFailed": "Failed to save feedback",
    "customFieldsTableMissing": "The workspace_custom_field_definitions table does not exist yet. Run Supabase migrations."
  },
  "modules": {
    "overview": "Overview",
    "tasks": "Tasks",
    "documents": "Documents",
    "bookings": "Bookings",
    "packing": "Packing",
    "resources": "Resources",
    "finances": "Finances",
    "settings": "Settings"
  },
  "emptyStates": {
    "tasks": {
      "title": "No tasks yet",
      "description": "Add tasks or let the system create them automatically based on your visa.",
      "action": "Add task"
    },
    "tasksFilter": {
      "title": "No tasks found",
      "description": "Try adjusting filters or search for a different term.",
      "clearAction": "Clear filters"
    },
    "moduleSearch": {
      "title": "No matches",
      "description": "Try a different search term.",
      "clearAction": "Clear search"
    },
    "documents": {
      "title": "No documents added",
      "description": "Required documents appear automatically when you create a workspace with a visa type.",
      "action": "Add document"
    },
    "bookings": {
      "title": "No bookings yet",
      "description": "Add bookings manually or import them from confirmation emails.",
      "action": "Add booking",
      "secondaryAction": "Import from email/PDF"
    },
    "packing": {
      "title": "Packing list is empty",
      "description": "Add items or let the system suggest them based on your destination.",
      "action": "Add item"
    },
    "resources": {
      "title": "No saved resources",
      "description": "Save useful links about visas, housing, banking, and more.",
      "action": "Add resource"
    },
    "resourcesSearch": {
      "title": "No resources match",
      "description": "Try another search term or clear filters.",
      "clearAction": "Clear search & filters"
    },
    "dashboard": {
      "title": "Create your first plan",
      "description": "Organize your move, study, or trip in one place."
    }
  },
  "bookings": {
    "searchPlaceholder": "Search bookings…",
    "viewLayoutLabel": "Bookings view",
    "viewList": "List",
    "viewCalendar": "Calendar",
    "newBooking": "New booking",
    "addBooking": "Add booking",
    "editBooking": "Edit booking",
    "noBookingsTitle": "No bookings yet",
    "noBookingsDesc": "Track flights, accommodation, cars, appointments and other reservations.",
    "addFirstBooking": "Add booking",
    "titleLabel": "Title",
    "titlePlaceholder": "e.g. Flight GRU → LIS",
    "typeLabel": "Type",
    "statusLabel": "Status",
    "providerLabel": "Provider",
    "providerPlaceholder": "e.g. GOL, Airbnb, Hertz",
    "dateLabel": "Date / Departure",
    "checkoutDateLabel": "End date (optional)",
    "confirmationLabel": "Confirmation / Ref",
    "confirmationPlaceholder": "PNR, booking code...",
    "costLabel": "Cost",
    "currencyLabel": "Currency",
    "notesLabel": "Notes",
    "notesPlaceholder": "Additional details...",
    "typeFlight": "Flight",
    "typeAccommodation": "Accommodation",
    "typeCar": "Car",
    "typeAppointment": "Consulate / Appt.",
    "typeOther": "Other",
    "statusPending": "Pending",
    "statusConfirmed": "Confirmed",
    "statusCancelled": "Cancelled",
    "save": "Save",
    "cancel": "Cancel",
    "delete": "Delete",
    "create": "Add",
    "conf": "Conf",
    "groupByType": "By type",
    "groupByDate": "By date"
  },
  "finances": {
    "emptyState": {
      "title": "No budget set yet",
      "description": "Add a total budget to track your spending progress.",
      "descriptionWithCosts": "You already have costs recorded — add a total budget to track spending against them.",
      "cta": "Set budget"
    },
    "searchPlaceholder": "Search cost breakdown…",
    "noBudgetSetTitle": "No budget set yet",
    "noBudgetSetDesc": "Add a total budget to track your spending progress.",
    "noBudget": "No trip budget yet",
    "noBudgetDesc": "Set a total budget to track spending against tasks, bookings, and timeline costs.",
    "noBudgetTrackHint": "No budget set — add a total budget to track spending.",
    "setBudget": "Set a budget",
    "budgetWarning": "You have used most of your trip budget. Review costs before adding more.",
    "overBudget": "Committed costs exceed your trip budget. Adjust the budget or reduce spending.",
    "noItemsWithCost": "No items with a cost in this category.",
    "totalBudget": "Total budget",
    "committed": "Committed",
    "remaining": "Remaining",
    "breakdownTitle": "Cost breakdown",
    "groupTasks": "Tasks",
    "groupBookings": "Bookings",
    "groupTimeline": "Timeline",
    "subtotal": "Subtotal",
    "fxApproxHint": "Approximate conversion ({{source}}), Frankfurter rates as of {{date}}.",
    "fxSourceCached": "cached rates",
    "fxSourceLive": "live rates",
    "subtotalCurrency": "Subtotal ({{currency}})",
    "costCertainty": {
      "estimated": "Estimated",
      "unknown": "Cost unknown",
      "verified": "Verified amount",
      "user": "You entered"
    },
    "overviewTitle": "Budget overview",
    "overviewSubtitle": "Committed includes tasks with a recorded cost. Estimated amounts are labeled separately from verified fees.",
    "staleRatesBadge": "Stale exchange rates",
    "amountLabel": "Total amount",
    "currency": "Currency",
    "notesLabel": "Notes",
    "notesPlaceholder": "Optional notes…",
    "saveBudget": "Save budget",
    "budgetSaved": "Budget saved.",
    "editBudget": "Edit budget",
    "cancel": "Cancel",
    "validationAmount": "Enter a valid budget amount (0 or greater).",
    "saveFailed": "Could not save budget.",
    "healthTitle": "Trip Budget",
    "noBudgetShort": "No budget set",
    "goToFinances": "Set budget →",
    "viewFinancesCta": "View finances →",
    "healthCommittedBetween": "of",
    "healthCommittedSuffix": "committed",
    "healthRemaining": "{{amount}} remaining",
    "ratesLoading": "Exchange rates are loading, please try again in a moment.",
    "fxLoadingOptions": "Loading currency list…",
    "overviewFxLoading": "Loading exchange rates…",
    "convertingCosts": "Converting all task and timeline costs to {{currency}}…",
    "totalTasksInWorkspaceCurrency": "Total in {{currency}} (tasks)",
    "ratesAsOf": "Rates as of {{date}}",
    "ratesLastUpdated": "Exchange rates last updated {{date}}",
    "equationTasks": "Tasks",
    "equationBookings": "Bookings & events",
    "equationTotal": "Total committed",
    "equationFootnote": "Converted to {{currency}} using stored exchange rates.",
    "timelineEmptyHelper": "Costs from tasks and priced timeline items appear here once you add amounts.",
    "timelineEmptyTasksCta": "Open tasks",
    "notConvertedAmount": "{{amount}} (not converted)",
    "notConvertedMissingRate": "No exchange rate to the display currency.",
    "exchangeRatesLoadError": "Could not load exchange rates for totals.",
    "overBudgetShort": "Over budget",
    "unknownCostsHint": "{{count}} task(s) have an unknown or unconfirmed cost (not counted as $0).",
    "fxIncompleteHint": "Some costs could not be converted to the budget currency and are excluded from the progress total."
  },
  "packing": {
    "searchPlaceholder": "Search packing list…",
    "viewLayoutLabel": "Packing view",
    "viewChecklist": "Checklist",
    "viewGrid": "Grid",
    "newItem": "New item",
    "addItem": "Add item",
    "editItem": "Edit item",
    "noItemsTitle": "Empty list",
    "noItemsDesc": "Add items so you don't forget anything on your move.",
    "addFirstItem": "Add item",
    "itemLabel": "Item",
    "itemPlaceholder": "e.g. Passport",
    "categoryLabel": "Category",
    "quantityLabel": "Quantity",
    "notesLabel": "Notes",
    "notesPlaceholder": "Observations...",
    "packed": "Packed",
    "unpack": "Unpack",
    "overallProgress": "{{packed}} of {{total}} items packed",
    "allPacked": "All packed! 🎉",
    "categoryProgress": "{{packed}}/{{total}}",
    "save": "Save",
    "cancel": "Cancel",
    "delete": "Delete",
    "create": "Add",
    "catDocumentos": "Documents",
    "catRoupas": "Clothes",
    "catEletronicos": "Electronics",
    "catMedicamentos": "Medications",
    "catHigiene": "Hygiene",
    "catDinheiro": "Money",
    "catOutros": "Other"
  },
  "resources": {
    "viewLayoutLabel": "Resources view",
    "viewGrid": "Grid",
    "viewList": "List",
    "viewSearch": "Search",
    "newResource": "New resource",
    "addResource": "Add resource",
    "editResource": "Edit resource",
    "noResourcesTitle": "No resources yet",
    "noResourcesDesc": "Save useful links like visa sites, community groups, guides, and tools.",
    "addFirstResource": "Add resource",
    "titleLabel": "Title",
    "titlePlaceholder": "e.g. AIMA Portal",
    "urlLabel": "URL",
    "urlPlaceholder": "https://...",
    "categoryLabel": "Category",
    "descriptionLabel": "Description",
    "descriptionPlaceholder": "What is this resource for?",
    "isFavoriteLabel": "Favorite",
    "searchPlaceholder": "Search resources...",
    "destinationFor": "Showing resources for {{destination}}",
    "destinationPrioritized": "Prioritized for {{destination}} — switch to “All resources” to see everything.",
    "destinationFilterDestination": "Destination only",
    "destinationFilterAll": "All resources",
    "destinationEmptyTitle": "No resources match this destination",
    "destinationEmptyDescription": "Try “All resources” to see every saved link, or add links that mention your destination in the title, category, or description.",
    "destinationEmptyAction": "Show all resources",
    "allCategories": "All categories",
    "favoritesOnly": "Favorites only",
    "openLink": "Open",
    "favorite": "Favorite",
    "unfavorite": "Unfavorite",
    "save": "Save",
    "cancel": "Cancel",
    "delete": "Delete",
    "create": "Add",
    "catVisto": "Visa",
    "catMoradia": "Housing",
    "catBancario": "Banking",
    "catSaude": "Health",
    "catTransporte": "Transport",
    "catEducacao": "Education",
    "catOutros": "Other",
    "noResults": "No results found"
  },
  "itinerary": {
    "typeVisa": "Consulate / Visa",
    "typeFlight": "Flight",
    "typeAccommodation": "Accommodation",
    "typeBank": "Bank",
    "typeHealth": "Health",
    "typeSchool": "School / University",
    "typeCustom": "Other"
  },
  "docs": {
    "autoGeneratedBanner": "These documents are auto-generated from your visa tasks. To edit requirements, go to <tasks>Tasks</tasks>.",
    "visaRequirementsFromTasks": "Visa Requirements (from Tasks)",
    "searchPlaceholder": "Search documents…",
    "uploadTitle": "Drag files here or click to upload",
    "uploadSubtitle": "Supports PDF, JPG, PNG (max. 10MB per file)",
    "uploadButton": "Select files",
    "uploadSuccess": "File added",
    "linkedTasksCount_one": "Linked to {{count}} task",
    "linkedTasksCount_other": "Linked to {{count}} tasks",
    "uploadSizeError": "File too large (max. 10MB)",
    "viewLayoutLabel": "Documents view",
    "viewChecklist": "Checklist",
    "viewTable": "Table",
    "viewProgress": "Progress",
    "newDoc": "New document",
    "addDoc": "Add document",
    "editDoc": "Edit document",
    "deleteDoc": "Remove document",
    "confirmDelete": "Remove this document? This cannot be undone.",
    "noDocsTitle": "No documents yet",
    "noDocsDesc": "Track documents like passports, certificates, bank statements, and more.",
    "addFirstDoc": "Add document",
    "nameLabel": "Name",
    "namePlaceholder": "e.g. Valid passport",
    "categoryLabel": "Category",
    "categoryPlaceholder": "Select a category",
    "statusLabel": "Status",
    "deadlineLabel": "Deadline",
    "notesLabel": "Notes",
    "notesPlaceholder": "Notes about this document...",
    "isRequiredLabel": "Required",
    "save": "Save",
    "cancel": "Cancel",
    "delete": "Remove",
    "create": "Add",
    "saving": "Saving…",
    "docAdded": "Document added.",
    "docSaved": "Document saved.",
    "docRemoved": "Document removed.",
    "toast": {
      "added": "Document added.",
      "saved": "Document saved.",
      "deleted": "Document removed."
    },
    "statusNeeded": "Pending",
    "statusInProgress": "In progress",
    "statusObtained": "Obtained",
    "statusSubmitted": "Submitted",
    "statusApproved": "Approved",
    "catIdentidade": "Identity",
    "catFinanceiro": "Financial",
    "catSaude": "Health",
    "catLegal": "Legal",
    "catAlojamento": "Housing",
    "catEducacao": "Education",
    "catOutros": "Other",
    "deadline": "Deadline",
    "overdue": "Overdue",
    "daysLeft": "{{n}} days",
    "required": "Required",
    "optional": "Optional",
    "colName": "Document",
    "colCategory": "Category",
    "colStatus": "Status",
    "colDeadline": "Deadline",
    "colNotes": "Notes",
    "colActions": "Actions",
    "progressTitle": "Progress by category",
    "overallProgress": "Overall progress",
    "totalDocs": "{{done}}/{{total}} documents"
  },
  "documents": {
    "infoBanner": {
      "text": "These documents are auto-generated from your visa tasks. To edit them, go to the Tasks view.",
      "cta": "Go to Tasks",
      "dismissAria": "Dismiss notice"
    }
  },
  "tasks": {
    "viewLayoutLabel": "Task view",
    "aiGeneratedWarning": "Tasks based on AI data — verify requirements.",
    "viewList": "List",
    "viewKanban": "Kanban",
    "viewCalendar": "Calendar",
    "viewGantt": "Timeline (Gantt)",
    "viewTable": "Table",
    "gantt": {
      "groupBySection": "Section",
      "groupByCategory": "Category",
      "groupByNone": "None",
      "groupByLabel": "Group by",
      "allTasksLane": "All tasks",
      "noSection": "No section"
    },
    "ganttEmptyWorkspace": "No tasks in this workspace yet.",
    "ganttNoDueDates": "No tasks have a due date yet. Set a due date in the task detail sheet and it will appear on this timeline.",
    "ganttNoDateSection": "Tasks without a due date",
    "searchPlaceholder": "Search tasks…",
    "bulk": {
      "selected": "{{count}} selected",
      "markDone": "Mark as done",
      "delete": "Delete selected",
      "clear": "Clear selection"
    },
    "pagination": {
      "rowsPerPage": "Rows per page",
      "page": "Page {{current}}",
      "of": "of {{total}}",
      "prev": "Previous",
      "next": "Next"
    },
    "table": {
      "selectAllPage": "Select all on this page",
      "selectRow": "Select “{{title}}”"
    },
    "meta": {
      "cost": "{{amount}} USD",
      "due": "{{date}}"
    },
    "listDescriptionShowMore": "Show more",
    "sectionTodo": "To do",
    "sectionInProgress": "In progress",
    "sectionDone": "Done",
    "sectionGeneral": "General",
    "sectionHeaderTaskCount_other": "{{count}} tasks",
    "sectionHeaderTaskCount_one": "{{count}} task",
    "priorityNone": "No priority",
    "priorityLow": "Low",
    "priorityMedium": "Medium",
    "priorityHigh": "High",
    "priorityUrgent": "Urgent",
    "dueToday": "Today",
    "dueTomorrow": "Tomorrow",
    "overdue": "Overdue",
    "addTask": "Add task",
    "newTask": "New task",
    "extractedFromDocument": "Extracted from document",
    "fillFromDoc": "Extract from document",
    "fillFromDocHint": "Paste an email, PDF text, or any text that describes the task…",
    "fillFromDocSuccess": "Fields filled in! Review and save.",
    "fillFromDocError": "Could not extract a task. Try with more detailed text.",
    "fillFromDocFileTooLarge": "File too large. Maximum size is 10 MB.",
    "fillFromDocFileType": "Unsupported file type. Use PDF, JPG, PNG, or WebP.",
    "fillFromDocRemoveFile": "Remove file",
    "documents": {
      "sectionTitle": "Documents",
      "attachExisting": "Attach existing document",
      "uploadAndAttach": "Upload and attach",
      "noDocuments": "No documents linked yet.",
      "detach": "Remove link",
      "searchPlaceholder": "Search documents…",
      "attachSelected": "Attach selected",
      "uploading": "Uploading…"
    },
    "tips": {
      "toggle": "Tips",
      "successTitle": "Tips for success",
      "mistakesTitle": "Common mistakes"
    },
    "editTask": "Edit task",
    "deleteTask": "Delete task",
    "confirmDelete": "Delete this task? This action cannot be undone.",
    "titleLabel": "Title",
    "titlePlaceholder": "Task name",
    "descriptionLabel": "Description",
    "descriptionPlaceholder": "Add a description...",
    "cost": "Cost",
    "currency": "Currency",
    "costHint": "This cost will be tracked in your trip budget",
    "dueDateLabel": "Due date",
    "fields": {
      "dateRangeLabel": "Date range",
      "dateRangeHelp": "Choose a single day or a range.",
      "planningDateHint": "This date is a Wundrlst planning recommendation, not a government deadline.",
      "leanScheduleHint": "Use the date range above for main travel, stay, or appointment dates. Older per-field dates may still exist in saved data.",
      "leanCostHint": "Use cost above for fees and totals. Category-specific price fields may still exist in saved data but are hidden here."
    },
    "priorityLabel": "Priority",
    "sectionLabel": "Section",
    "sectionPlaceholder": "e.g. Visa, Housing...",
    "save": "Save",
    "cancel": "Cancel",
    "delete": "Delete",
    "create": "Create",
    "saving": "Saving…",
    "deleting": "Deleting…",
    "taskCreated": "Task created.",
    "taskSaved": "Task saved.",
    "taskDeleted": "Task deleted.",
    "toast": {
      "created": "Task created.",
      "saved": "Task saved.",
      "completedWithUndo": "Task completed",
      "statusUpdated": "Status updated",
      "deleted": "Task deleted",
      "deletedWithUndo": "Task deleted"
    },
    "validationTitle": "Add a task title to continue",
    "validationCategoryFieldRequired": "{{field}} is required",
    "validationCategoryInvalidUrl": "Enter a valid URL",
    "validationCategoryInvalidEmail": "Enter a valid email address",
    "validationCategoryInvalidPhone": "Enter a valid phone number",
    "validationCategoryInvalidTime": "Use time in HH:MM (e.g. 14:30)",
    "validationCategoryInvalidNumber": "Enter a valid number",
    "validationCategoryNumberRange": "Number is out of the allowed range",
    "validationCategoryLengthMin": "{{field}} is too short",
    "validationCategoryLengthMax": "{{field}} is too long",
    "noTasks": "No tasks yet",
    "noTasksDesc": "Tasks help you track every step — from visa applications to booking flights.",
    "addFirstTask": "Add first task",
    "unscheduled": "Unscheduled",
    "colTitle": "Title",
    "colSection": "Section",
    "colPriority": "Priority",
    "colDueDate": "Due date",
    "colStatus": "Status",
    "colActions": "Actions",
    "edit": "Edit",
    "mon": "Mon",
    "tue": "Tue",
    "wed": "Wed",
    "thu": "Thu",
    "fri": "Fri",
    "sat": "Sat",
    "sun": "Sun"
  },
  "auth": {
    "signIn": "Sign in",
    "signUp": "Create account",
    "email": "Email",
    "password": "Password",
    "fullName": "Full name",
    "continueWithGoogle": "Continue with Google",
    "continueWithApple": "Continue with Apple",
    "verifyEmail": "Verify your email",
    "verifyEmailDesc": "We sent a verification link to",
    "resendEmail": "Resend email",
    "emailVerified": "Email verified!",
    "acceptTerms": "You must accept the terms to continue.",
    "welcomeBack": "Welcome back",
    "welcomeBackDesc": "Sign in to your Wundrlst account to continue.",
    "createAccount": "Create your account",
    "createAccountDesc": "Start your first Wundrlst workspace in minutes.",
    "forgotPassword": "Forgot your password?",
    "forgotPasswordTitle": "Forgot password",
    "forgotPasswordDesc": "We'll email you a secure link to choose a new password.",
    "sendResetLink": "Send reset link",
    "sendingReset": "Sending…",
    "resetEmailSentTitle": "Check your email",
    "resetEmailSentMessage": "If an account exists for {{email}}, you'll receive an email with a reset link.",
    "backToSignIn": "Back to sign in",
    "rememberedPassword": "Remembered it?",
    "resetEmailError": "Unable to send reset email.",
    "enterPassword": "Enter your password",
    "passwordMinLength": "At least 8 characters",
    "passwordHint": "Use at least 8 characters.",
    "signingIn": "Signing in…",
    "login": "Login",
    "creating": "Creating…",
    "or": "or",
    "iAccept": "I accept the",
    "termsConditions": "Terms & Conditions",
    "andThe": "and the",
    "privacyLink": "Privacy Policy",
    "backToHome": "← Back to home",
    "signupVerifyBannerTitle": "Check your email",
    "signupVerifyBannerBody": "We sent a verification link to {{email}}. Open it to confirm your account.",
    "signupVerifyBannerHint": "The link expires in 24 hours.",
    "signupVerifyBannerGoToSignIn": "Sign in after you verify →"
  },
  "blogPost": {
    "notFoundTitle": "Article not found",
    "notFoundDesc": "We could not find this article. It may have been removed or the CMS is not configured.",
    "backToBlog": "Back to blog",
    "home": "Home",
    "minRead": "{{count}} min read",
    "publishedOn": "Published on",
    "backToBlogLink": "← Back to blog",
    "bodySoon": "Full content coming soon — add the post body in Sanity Studio."
  },
  "blog": {
    "category": {
      "fallback": "Blog",
      "all": "All",
      "visto": "Visa",
      "moradia": "Housing",
      "financas": "Finance",
      "destinos": "Destinations",
      "dicas": "Tips",
      "comunidade": "Community",
      "vistos": "Visas (legacy)",
      "relocation": "Relocation (legacy)"
    },
    "list": {
      "title": "Blog {{app}}",
      "intro": "Practical guides on visas, moving, and destinations — not legal advice, just context to help you organize your trip.",
      "configureHintPrefix": "New posts soon. Configure",
      "configureCode": "VITE_SANITY_PROJECT_ID",
      "configureHintSuffix": "in your environment to load posts from the CMS.",
      "emptySoon": "New posts soon.",
      "prev": "Previous",
      "next": "Next",
      "page": "Page {{current}} of {{total}}"
    }
  },
  "legal": {
    "termsTitle": "Terms & Conditions",
    "privacyTitle": "Privacy Policy",
    "cookiesTitle": "Cookie Policy",
    "breadcrumbHome": "Home",
    "breadcrumbTerms": "Terms of use",
    "breadcrumbPrivacy": "Privacy policy",
    "breadcrumbCookies": "Cookie policy",
    "lastUpdated": "Last updated: {{date}}",
    "lastUpdatedPlaceholder": "Last updated: to be published.",
    "backToHome": "← Back to home",
    "contentSoon": "Full content will be published soon.",
    "dialogDescription": "Legal document viewer",
    "openFullPage": "Open full page in new tab"
  },
  "common": {
    "undo": "Undo",
    "linkCopied": "Link copied",
    "linkCopyFailed": "Could not copy link",
    "save": "Save",
    "cancel": "Cancel",
    "delete": "Delete",
    "edit": "Edit",
    "add": "Add",
    "search": "Search",
    "loading": "Loading...",
    "error": "Something went wrong",
    "retry": "Try again",
    "done": "Done",
    "view": "View",
    "close": "Close",
    "confirm": "Confirm",
    "yes": "Yes",
    "no": "No",
    "back": "Back"
  },
  "views": {
    "list": "List",
    "kanban": "Kanban",
    "calendar": "Calendar",
    "table": "Table",
    "timeline": "Timeline",
    "grid": "Grid"
  },
  "verifyBanner": {
    "message": "Verify your email",
    "description": "We sent a verification link to",
    "resend": "Resend email",
    "resending": "Sending...",
    "resent": "Email sent! ✓",
    "error": "Failed to send. Try again.",
    "dismiss": "Dismiss"
  },
  "language": {
    "toggle": "Language",
    "pt": "PT",
    "en": "EN"
  },
  "landing": {
    "hero": {
      "eyebrow": "Wundrlst",
      "title": "Your move, planned. Every step.",
      "subtitle": "Tell us where you're from and where you're going. We handle the rest — visa requirements, tasks, documents, budget.",
      "statsLine": "500+ routes · 30+ nationalities · 80+ destinations",
      "signUp": "Sign up",
      "signIn": "Sign in",
      "bookDemo": "Schedule demo",
      "imageAltFromCms": "Hero image for Wundrlst",
      "imageAltStock": "Illustrative stock photo — not a screenshot of the Wundrlst app."
    },
    "features": {
      "eyebrow": "Why Wundrlst",
      "title": "Powerful structure for a chaotic process",
      "subtitle": "Long moves and shorter trips both mean coordination. Wundrlst gives you a calm surface to track what matters — without pretending to replace qualified legal advice.",
      "card1Title": "One workspace per move",
      "card1Desc": "A dedicated space for each trip or relocation. Visas, documents, saved resources, tasks, and budget — connected in one calm place.",
      "card2Title": "Deadline tracking",
      "card2Desc": "Tasks have dates. See what's due next before it becomes urgent — visa appointment, lease renewal, or a key travel date.",
      "card3Title": "Private by default",
      "card3Desc": "Row-level security keeps every workspace tied to your account. Your sensitive travel documents stay yours alone.",
      "card4Title": "Checklists from visa data",
      "card4Desc": "Tell us your passport, destination, and visa type — we seed tasks from our visa database when the route exists. Edit everything to match your case.",
      "firstVisualScreenshotAlt": "Wundrlst workspace screenshot",
      "illustrativePhotoAlt": "Illustrative photo: {{title}}"
    },
    "journeys": {
      "eyebrow": "Journeys",
      "title": "Structured plans for real moves",
      "subtitle": "Each workspace connects visa planning, documents, saved resources, and budget — tuned for international routes, not generic to-do noise.",
      "cta": "Create free account"
    },
    "testimonials": {
      "eyebrow": "Testimonials",
      "title": "What early movers are saying",
      "subtitle": "Published quotes from Studio appear here when available. Otherwise you will see short, non-attributed examples of common uses — not real customer testimonials.",
      "ratingLabel": "{{count}} out of 5 stars",
      "selectLabel": "Choose testimonial",
      "dotLabel": "Show testimonial {{index}} of {{total}}",
      "q1": "We finally stopped losing embassy PDFs in chat threads. One workspace per move made handoffs between me and my partner obvious.",
      "q2": "The first checklist from visa data saved us an afternoon. We still edit everything — but we did not start from a blank page.",
      "q3": "Dates and tasks in the same column as documents sounds simple. It is — and that is why we kept using it past the visa stage.",
      "q4": "For a shorter Schengen trip we hid modules we did not need. Same product, less noise — exactly what we wanted.",
      "fallbackName": "Wundrlst user",
      "fallbackRole": "Early access",
      "fallbackCompany": "Illustrative example",
      "fallbackQuote1": "Keep visa requirements, documents, and trip dates in one workspace instead of scattered notes and threads.",
      "fallbackQuote2": "Use saved links and a simple budget view alongside tasks so the move stays visible end to end.",
      "fallbackQuote3": "Walk through guided setup for a route, then adjust tasks and deadlines as your plan changes."
    },
    "cta": {
      "eyebrowFaded": "Ready when you are.",
      "title": "Organise your next move in one workspace",
      "subtitle": "Create a free account and keep visas, documents, and dates in one calm place.",
      "createAccount": "Create free account",
      "login": "Log in",
      "bookDemo": "Schedule a demo"
    },
    "pricing": {
      "eyebrow": "Pricing",
      "title": "Start free while we sharpen the product",
      "subtitle": "No credit card required to explore. Pricing may evolve as Wundrlst grows — early accounts keep generous access.",
      "planName": "Wundrlst",
      "planBadge": "Early access",
      "planDesc": "For individuals and households planning a move or structured trip.",
      "perMonth": "/month",
      "cta": "Get started free",
      "included": "What's included:",
      "betaNote": "Includes core workspace modules and visa-route updates while in beta.",
      "teamsNote": "Teams and advisors: tell us how you work — we are listening for the next tier.",
      "feature1": "Workspaces for each move or trip",
      "feature2": "Tasks, documents, saved resources, and budget tracking",
      "feature3": "Task and visa checklists you can adjust anytime",
      "feature4": "Private to your account (RLS-backed)",
      "softDemoCta": "Not sure? Book a demo →"
    },
    "visaExplorer": {
      "eyebrow": "Visa checker",
      "title": "Check visa requirements for your trip",
      "subtitle": "Search 600+ routes — no account needed"
    },
    "faq": {
      "eyebrow": "FAQ",
      "title": "Common questions",
      "subtitle": "Straight answers about what Wundrlst does — and what it deliberately does not do.",
      "q1": "Is Wundrlst legal or immigration advice?",
      "a1": "No. Wundrlst is a planning workspace (tasks, documents, dates, links). It does not replace lawyers, registered migration agents, or official government guidance. Always confirm requirements with embassies, consulates, or qualified professionals.",
      "q2": "Do you book flights, hotels, or appointments for me?",
      "a2": "No. You keep confirmations and dates in your workspace. Wundrlst is not a travel agency, booking engine, or concierge service.",
      "q3": "Does Wundrlst guarantee visa approval or timelines?",
      "a3": "No. Timelines and visa hints shown in the product are orientation only. Processing times and rules change by country and case.",
      "q4": "Who is Wundrlst built for?",
      "a4": "People coordinating a serious international move or a structured trip: work relocation, study, family moves, Schengen-style visa trips, or shorter visa-free travel. Seeded tasks are starting points — everything is editable.",
      "q5": "Can I use it only for a short vacation?",
      "a5": "Yes. Choose a narrower focus (for example a short visa-free trip or a Schengen checklist) and skip workspace areas you do not need.",
      "q6": "What does Wundrlst set up for me?",
      "a6": "Wundrlst creates a personalised workspace with visa application, documents, flights, accommodation, budget, and key dates.",
      "q7": "Is my data private?",
      "a7": "Workspaces are tied to your account. Access is enforced with Supabase Row Level Security so you only read and write your own rows. Do not upload highly sensitive secrets unless you trust your device and network.",
      "stillQuestions": "Still have questions?",
      "stillQuestionsDesc": "Have questions or need assistance? Our team is here to help.",
      "contact": "Contact us"
    },
    "blogSection": {
      "tagline": "Guides",
      "heading": "Start with the problems we solve",
      "description": "Short reads on scattered plans, missed deadlines, and keeping trips in one workspace — then see how Wundrlst fits your flow.",
      "seeAll": "See all posts",
      "readMore": "Read more",
      "empty": "No articles yet — check back soon.",
      "noCover": "No cover",
      "fetchFailed": "Could not load posts from the blog. If this persists, check Sanity configuration and the browser console."
    },
    "footer": {
      "tagline": "Visas, documents, tasks, and dates in one calm workspace — for any move or complex trip.",
      "copyright": "All rights reserved.",
      "madeFor": "Made for people who plan big trips",
      "features": "Features",
      "pricing": "Pricing",
      "guides": "Guides",
      "blog": "Blog",
      "faq": "FAQ",
      "contact": "Contact",
      "terms": "Terms",
      "privacy": "Privacy",
      "cookies": "Cookies",
      "legalNavLabel": "Legal"
    },
    "emptyState": {
      "title": "Create your first plan",
      "desc": "Get started by planning your relocation, study abroad, or trip. We seed checklist tasks from visa data when we know the route — or start from scratch.",
      "blank": "Blank workspace",
      "browse": "Create workspace"
    }
  },
  "overview": {
    "customize": "Customize view",
    "customizeDescription": "Choose what to see on this workspace overview. Your choices are saved per trip.",
    "sectionReadiness": "Trip readiness (ring & breakdown)",
    "sectionInsights": "What to do next",
    "sectionTasks": "Tasks",
    "sectionDocuments": "Documents",
    "sectionBookings": "Bookings",
    "sectionPacking": "Packing",
    "sectionResources": "Resources",
    "resetDefault": "Show all",
    "close": "Done",
    "allHidden": "All overview sections are hidden. Restore the default layout to see tasks, documents, resources, and more.",
    "customizeHint": "Customize",
    "urgencyUrgent": "Urgent",
    "urgencyAttention": "Attention",
    "resetLayout": "Restore default layout",
    "statTasks": "{{count}} tasks",
    "statDocuments": "{{count}} documents",
    "statEvents": "{{count}} events",
    "statBookings": "{{count}} bookings",
    "statPackingItems": "{{count}} items",
    "statResources": "{{count}} saved",
    "addTask": "Add task",
    "addDocument": "Add document",
    "addBooking": "Add booking",
    "addPackingItem": "Add item",
    "addResource": "Add resource"
  },
  "workspaceOverviewPage": {
    "visaFreeBannerTitle": "No visa required for your trip",
    "visaFreeBannerBody": "Your passport gives you visa-free access to {{destination}} according to verified Wundrlst data. Always confirm with the official authority before you travel. Focus on the prep checklist below.",
    "visaBannerTitle": "No visa requirement data for this route yet",
    "visaBannerBody": "Wundrlst does not have visa metadata for your nationality and this workspace destination. Always confirm requirements with the issuing authority.",
    "visaBannerOnboarding": "Your saved onboarding status:",
    "visaBannerCta": "Review visa profile",
    "visaCardTitle": "Visa status",
    "cardOpenTasks": "Open tasks",
    "cardDocumentQueue": "Pending documents",
    "cardSoon": "Soon",
    "cardCollaborators": "Collaborators",
    "goToTasksCta": "Go to tasks →",
    "viewTasksCta": "View tasks →",
    "routeSnapshot": {
      "title": "Route snapshot",
      "origin": "From",
      "destination": "To",
      "visa": "Visa",
      "status": "Entry status",
      "processing": "Typical processing",
      "unknown": "—",
      "loading": "Loading route…",
      "mapAria": "Route from {{origin}} to {{destination}}",
      "weatherHint": "Approximate current conditions (Open-Meteo)",
      "missing": {
        "visa": "Not set",
        "status": "Not set",
        "processing": "Not available"
      }
    },
    "loading": "Loading overview…",
    "welcomeBannerTitle": "Your workspace is ready",
    "welcomeBannerGotIt": "Got it",
    "welcomeBannerLabels": {
      "tasks": "Tasks",
      "documents": "Documents",
      "finances": "Finances",
      "resources": "Resources",
      "settings": "Settings"
    },
    "welcomeBannerHints": {
      "tasksHint": "Visa + travel checklist",
      "documentsHint": "Files & uploads",
      "financesHint": "Budget vs costs",
      "resourcesHint": "Links & contacts",
      "settingsHint": "Workspace preferences"
    },
    "welcomeBannerFooter": "Start in Tasks for the checklist.",
    "tasksCardTitle": "Task completion",
    "tasksCardFraction": "{{done}} / {{total}}",
    "progressScaleStart": "0%",
    "progressScaleEnd": "100%",
    "nextCostTitle": "Next cost due",
    "nextCostDue": "Due date",
    "nextCostAmount": "Amount",
    "nextCostEmpty": "No upcoming tasks with a cost and due date.",
    "nextCostDaysUntil_one": "{{count}} day until due",
    "nextCostDaysUntil_other": "{{count}} days until due",
    "categoryBreakdownTitle": "Tasks by category",
    "statusDistributionTitle": "Tasks by status",
    "statusDonutAria": "Task status distribution",
    "taskStatus": {
      "todo": "To do",
      "in_progress": "In progress",
      "done": "Done"
    },
    "categories": {
      "visa_application": "Visa application",
      "document_collection": "Document collection",
      "accommodation": "Accommodation",
      "flight_booking": "Flights",
      "rental_car": "Rental car",
      "appointment": "Appointment",
      "bank_account": "Bank account",
      "registration": "Registration",
      "moving_logistics": "Moving logistics",
      "general": "General"
    },
    "collaboratorFallback": "User",
    "stickyBarAria": "Workspace summary",
    "voaBannerTitle": "Visa on arrival — not visa-free",
    "voaBannerBody": "For {{destination}}, you still need to obtain a visa at the border or airport. Fees and documents may apply. Confirm with the destination authority before you travel.",
    "officialSourceLink": "Open official source",
    "visaTrustLastVerified": "Last verified {{date}}",
    "routeTrustTitle": "Route requirements",
    "routeTrustVerified": "Verified route data for {{destination}} ({{visa}}). Always confirm with the official authority before you travel.",
    "routeTrustLastReviewed": "Last reviewed: {{date}}",
    "routeTrustNoOfficialUrl": "No official source URL stored for this route yet.",
    "routeTrustNeedsAttention": "{{count}} open tasks need attention (overdue or high priority).",
    "documentsCardFraction": "{{pending}} pending · {{total}} total",
    "documentsCardEmpty": "No documents seeded yet. Add what this route requires.",
    "documentsCardHint": "Documents linked to your preparation checklist.",
    "viewDocumentsCta": "View documents →",
    "nextCostInline": "Next cost: {{title}} · {{amount}} · {{date}}"
  },
  "chatbot": {
    "minimized": "Assistant",
    "title": "Wundrlst Assistant",
    "settings": "Settings",
    "close": "Close",
    "settingsSummaryTitle": "Assistant settings",
    "settingsSummaryDesc": "Topics, tone, and sources are configured per workspace.",
    "openFullSettings": "Open full settings",
    "clearLocal": "Clear chat",
    "empty": "Ask a question about your tasks, documents, or timeline.",
    "sources": "Sources",
    "voteHelpful": "Helpful",
    "voteNotHelpful": "Not helpful",
    "copy": "Copy",
    "copied": "Copied ✓",
    "copyFailed": "Could not copy.",
    "typing": "Assistant is typing",
    "retry": "Retry",
    "placeholder": "Ask a question…",
    "send": "Send",
    "errorNoConnection": "No connection. Try again.",
    "thanks": "Thanks!"
  },
  "chatbotSettings": {
    "loading": "Loading assistant settings…",
    "title": "AI Assistant — Settings",
    "subtitle": "Configure what the assistant can discuss, tone, and language preferences for this workspace.",
    "back": "Back to workspace settings",
    "saved": "Settings saved ✓",
    "saving": "Saving…",
    "save": "Save changes",
    "reset": "Restore defaults",
    "resetConfirm": "Restore default assistant settings for this workspace?",
    "resetDone": "Defaults restored ✓",
    "topicsTitle": "What can the assistant discuss?",
    "topicsDesc": "Disable topics you don’t want the assistant to answer in this workspace.",
    "topicVisa": "Visa & requirements",
    "topicDocuments": "Required documents",
    "topicTasks": "Tasks & progress",
    "topicBookings": "Bookings",
    "topicPacking": "Packing",
    "topicResources": "Resources & links",
    "topicGeneral": "General tips",
    "toneTitle": "Response style",
    "toneDesc": "How the assistant should sound.",
    "toneFriendly": "Friendly",
    "toneFriendlyExample": "“Hi! Let’s get this organized.”",
    "toneProfessional": "Professional",
    "toneProfessionalExample": "“I can help with structured information about…”",
    "toneBrief": "Brief",
    "toneBriefExample": "“[Information only]”",
    "lengthTitle": "Response length",
    "lengthDesc": "How detailed replies should be.",
    "lengthBrief": "Brief",
    "lengthBriefExample": "1–2 sentences",
    "lengthMedium": "Medium",
    "lengthMediumExample": "2–3 paragraphs",
    "lengthDetailed": "Detailed",
    "lengthDetailedExample": "Full explanation",
    "sourcesTitle": "Sources",
    "sourcesDesc": "Show official links when available.",
    "includeSources": "Cite official sources",
    "languagesTitle": "Languages",
    "languagesDesc": "Preferred response languages.",
    "langPt": "Portuguese",
    "langEn": "English",
    "langEs": "Spanish",
    "actionsTitle": "Actions",
    "disclaimer": "The assistant uses your workspace data. It will never provide specific legal or medical advice. For legal questions, consult a lawyer."
  },
  "extraction": {
    "title": "Import document or email",
    "via": "Via:",
    "textLabel": "Paste email or document text",
    "textPlaceholder": "Paste email content, booking confirmation, visa document text...",
    "or": "or drag a file",
    "dropzoneHint": "Drag PDF or image here",
    "dropzoneClick": "or click to select",
    "dropzoneTypes": "JPG, PNG, PDF (max 10 MB)",
    "extract": "Extract data",
    "extracting": "Extracting data...",
    "cancel": "Cancel",
    "close": "Close",
    "startOver": "Extract another",
    "confidence": "Confidence",
    "errorTitle": "Extraction error",
    "saveAsBooking": "Save as booking",
    "addToTimeline": "Add to Timeline",
    "addToTasks": "Save as task",
    "saveDocumentAndTask": "Save document & create task",
    "saveAsDocument": "Save as document",
    "importDocument": "Import document",
    "importTask": "Import task",
    "importTaskHint": "Paste an email, notice, instruction, or any text that describes a task.",
    "taskDetected": "Task detected",
    "createTask": "Create task too",
    "taskTitle": "Title",
    "taskDescription": "Description",
    "taskDueDate": "Due date",
    "taskCategory": "Category",
    "taskPriority": "Priority",
    "taskSection": "Section",
    "saveTask": "Save task",
    "discard": "Discard",
    "taskSaved": "Task created!",
    "taskAndDocumentSaved": "Task created from this document. You can open it in Tasks (including the timeline view) and the file is kept under Documents.",
    "savedWorkSuccess": "Saved to your workspace.",
    "savedWorkHint": "A task was created (and your file is stored under Documents when you uploaded one). Open Tasks → Timeline (Gantt) to see dates on the schedule.",
    "reviewConfirm": "I have reviewed the extracted data and it looks correct (or I edited the source before saving).",
    "lowConfidenceNote": "Confidence is low. Double-check all fields before saving to your workspace."
  },
  "header": {
    "features": "Features",
    "pricing": "Pricing",
    "guides": "Guides",
    "blog": "Blog",
    "faq": "FAQ",
    "contact": "Contact",
    "signIn": "Sign in",
    "signUp": "Sign up",
    "startFree": "Start for free",
    "goToApp": "Go to app",
    "openMenu": "Open menu",
    "marketingNavLabel": "Site navigation",
    "overview": "Overview",
    "allTrips": "All trips",
    "signOut": "Sign out",
    "bookDemo": "Schedule demo"
  },
  "bookDemo": {
    "title": "Book a demo",
    "subtitle": "See Wundrlst in action. 30 minutes, no obligation.",
    "fieldFullName": "Full name",
    "fieldEmail": "Email",
    "fieldPhone": "Phone / WhatsApp",
    "fieldDestination": "Destination country",
    "fieldVisaType": "Visa type",
    "fieldMessage": "Message / context",
    "submit": "Schedule demo",
    "success": "We received your request! We will get back to you within 24 hours.",
    "bullet1": "Full organization for international relocation",
    "bullet2": "Automatic extraction of documents and reservations",
    "bullet3": "Visa routes by passport and destination",
    "bulletsTitle": "What you will see",
    "validation": "Please enter a valid name and email.",
    "error": "Something went wrong. Please try again.",
    "emailSubject": "Wundrlst — demo request",
    "messageFallback": "(No additional message.)"
  },
  "settings": {
    "title": "Settings",
    "subtitle": "General preferences, trip defaults, notifications, plan usage, account security, and AI defaults for new workspaces. Language, currency, and theme live in the account menu.",
    "breadcrumbRoot": "Settings",
    "navLabel": "Settings sections",
    "nav": {
      "general": "General",
      "ai": "AI / Chatbot"
    },
    "page": {
      "general": {
        "title": "General settings",
        "lead": "Language, appearance, default currency, and trip preferences — all in one place."
      },
      "ai": {
        "title": "AI & Chatbot"
      }
    },
    "ai": {
      "description": "These AI settings define default behavior for the Wundrlst assistant across your workspaces. Individual workspaces may still have their own overrides for now.",
      "defaultsHeading": "Defaults for new workspaces",
      "defaultsDescription": "When we roll out account-wide AI defaults, new workspaces will start from these values. Today, only this preview is stored on your device for the session.",
      "visaContextLabel": "Auto-use visa context in answers",
      "visaContextDesc": "When enabled, the assistant may use visa and immigration context by default for new workspaces (mirrors the per-workspace “Visa & requirements” topic).",
      "saveDefaults": "Save defaults",
      "stubSaved": "Saved for this session only — sync across devices is coming later."
    },
    "navTripDefaults": "Trip defaults",
    "navNotifications": "Notifications",
    "navPlan": "Plan & usage",
    "navAccount": "Account & security",
    "tripDefaultsTitle": "Trip defaults",
    "tripDefaultsDescription": "Country of residence sets your default currency for budgets and estimates.",
    "tripDefaultsCurrencyHint": "Default currency follows your country of residence. Change currency anytime from the account menu (sidebar or header).",
    "toastTripDefaultsSaved": "Trip defaults saved",
    "changePhoto": "Change photo",
    "fullName": "Full name",
    "email": "Email",
    "changeEmail": "Change email",
    "emailReadonlyHint": "Use the change flow to update your sign-in address.",
    "countryResidence": "Country of residence",
    "preferredCurrency": "Default currency",
    "save": "Save",
    "cancel": "Cancel",
    "sendLink": "Send confirmation",
    "toastProfileUpdated": "Profile updated ✓",
    "avatarBucketNotConfigured": "Photo storage is not ready (avatars bucket missing). Run Supabase migrations on this project, e.g. supabase db push, or create the “avatars” bucket in Dashboard → Storage.",
    "avatarUploadFailed": "Could not upload photo. Try again.",
    "avatarTooLarge": "Image must be 2 MB or smaller.",
    "toastPhotoUpdated": "Profile photo updated",
    "changeEmailDesc": "We will send a confirmation to the new address. You must verify it to finish the change.",
    "newEmail": "New email",
    "emailChangeConfirm": "Check your inbox to confirm the new email.",
    "language": "Language",
    "languageHint": "Change language from the account menu.",
    "theme": "Theme",
    "themeLight": "Light",
    "themeDark": "Dark",
    "themeSystem": "System",
    "dateFormat": "Date format",
    "dateFormatHint": "Used when picking and displaying dates for tasks, itineraries, and forms.",
    "generalAutoSaveHint": "Changes here save automatically. Destructive actions (sign out, delete account) stay on Account & security.",
    "saving": "Saving…",
    "timezone": "Time zone",
    "timezoneHint": "Used for reminders, due dates, and summaries in your local time.",
    "timezoneAuto": "Automatic (device)",
    "timezoneSearchPlaceholder": "Search time zones…",
    "generalLanguageHelper": "Interface language. Saved to your profile.",
    "generalThemeHelper": "Light, dark, or follow your device. Saved to your profile.",
    "generalCurrencyHelper": "Default for amounts and budgets where no workspace override applies.",
    "toastPreferencesSaved": "Display preferences saved",
    "notificationsTitle": "Notifications",
    "notificationsEmailPhase2": "Email delivery is coming later. We store your choices now; the overview urgency panel uses in-app signals.",
    "notifWeeklyDigest": "Weekly progress summary",
    "notifOverdueTasks": "Overdue tasks",
    "notifPendingDocuments": "Pending documents",
    "notifItineraryReminders": "Itinerary event reminders",
    "notifAppointmentReminders": "Appointment reminders (visas, consulates)",
    "notifHowFarAhead": "How far in advance?",
    "notifAdvance1d": "1 day",
    "notifAdvance3d": "3 days",
    "notifAdvance7d": "7 days",
    "toastNotificationsSaved": "Notification preferences saved",
    "planTitle": "Plan & usage",
    "planDescription": "Your current plan, workspace limits, and what each tier includes.",
    "planCurrent": "Current plan",
    "planNameFree": "Free",
    "planNamePro": "Pro",
    "planNameBusiness": "Business",
    "planUsage": "Workspaces",
    "planUsageValue": "{{used}} / {{limit}} workspaces used",
    "planUnlimitedWorkspaces": "Unlimited workspaces on your plan.",
    "planNextBillingPlaceholder": "Next billing: available when subscription billing is connected (Stripe).",
    "planFeatureFree1": "2 workspaces, 50 tasks per workspace, 3 extractions",
    "planFeatureFree2": "",
    "planFeatureFree3": "",
    "planFeaturePro1": "Unlimited workspaces",
    "planFeaturePro2": "Advanced features, email reminders",
    "planFeaturePro3": "More automation as we ship it",
    "planFeatureBiz1": "Team collaboration",
    "planFeatureBiz2": "Priority support",
    "planUpgradeCta": "Upgrade to Pro",
    "planManageSubscription": "Manage subscription",
    "planContactSales": "Contact sales",
    "accountTitle": "Account & security",
    "accountDescription": "Profile photo, name, email, password, and session.",
    "accountChangePassword": "Change password",
    "accountChangePasswordDesc": "Verify your current password, then set a new one (min. 8 characters).",
    "accountExportData": "Export my data",
    "accountExportDataDesc": "Download a JSON file with your profile and workspace data.",
    "accountSignOutDesc": "End your session on this device.",
    "accountDelete": "Delete account",
    "accountDeleteDesc": "Permanently remove your account and all associated data.",
    "accountNotSignedIn": "You must be signed in.",
    "passwordMin8": "New password must be at least 8 characters.",
    "passwordMustDiffer": "New password must be different from the current one.",
    "passwordMismatch": "New password and confirmation do not match.",
    "currentPasswordWrong": "Current password is incorrect.",
    "passwordChanged": "Password updated",
    "currentPassword": "Current password",
    "newPassword": "New password",
    "confirmPassword": "Confirm new password",
    "savePassword": "Update password",
    "exportSuccess": "Export started — check your downloads",
    "exportFailed": "Could not export data",
    "deleteTypeInvalid": "Type the confirmation word exactly.",
    "deleteWarning": "This cannot be undone. All your data will be removed, including trips and profile.",
    "deleteTypeInstruction": "Type {{code}} to confirm.",
    "deleteConfirm": "Delete forever",
    "deleteFailed": "Account deletion failed",
    "planExtractionUsage": "Document extractions: {{used}} / {{limit}} (free plan)"
  },
  "usage": {
    "sidebarWorkspaces": "{{used}}/{{max}} spaces used"
  },
  "upgrade": {
    "title": "Upgrade to Wundrlst Pro",
    "subtitle": "You've hit a limit on the free plan.",
    "proIncludes": "With Pro you get:",
    "bulletWorkspaces": "Unlimited workspaces",
    "bulletTasks": "Unlimited tasks",
    "bulletExtraction": "Unlimited document extraction",
    "bulletEmail": "Email reminders",
    "bulletSupport": "Priority support",
    "price": "Price: $19/month USD",
    "viewPlans": "View plans",
    "notNow": "Not now",
    "featureWorkspace": "You've used your free workspace allowance. Upgrade to create more.",
    "featureTasks": "You've reached the 50-task limit for this trip. Upgrade to Pro for unlimited tasks.",
    "featureExtraction": "You've used your free document extractions. Upgrade for unlimited extractions.",
    "loginRequiredExtraction": "Sign in to extract documents."
  },
  "marketing": {
    "contact": {
      "pageTitle": "Get in touch",
      "pageSubtitle": "We read every message. Share enough context and we'll point you to the right next step inside Wundrlst.",
      "infoTitle": "Contact information",
      "infoProduct": "Product & feedback",
      "infoPress": "Press & media",
      "infoRemote": "Remote-first",
      "infoRemoteBody": "No office. Our team works across time zones — São Paulo · Lisbon · wherever the work is.",
      "title": "Send us a message",
      "name": "Full name",
      "email": "Work email",
      "subject": "Subject",
      "message": "Message",
      "namePlaceholder": "Ana Silva",
      "emailPlaceholder": "ana@example.com",
      "subjectPlaceholder": "Bug, feature idea, or question",
      "messagePlaceholder": "Destination, timeline, what you tried…",
      "send": "Send message",
      "sending": "Sending…",
      "successTitle": "Message sent!",
      "successBody": "We will get back to you shortly.",
      "successToast": "Message sent! We will get back to you shortly.",
      "error": "Could not send. Please try again.",
      "validation": "All fields are required, and the email must be valid."
    },
    "waitlist": {
      "eyebrow": "Early access",
      "title": "Join the Wundrlst waitlist",
      "subtitle": "Be first to know when we open new beta slots.",
      "email": "Email",
      "nameOptional": "Name (optional)",
      "placeholderName": "Your name",
      "submit": "Join waitlist",
      "success": "🎉 You're on the waitlist!",
      "toastNew": "You're on the list — we will be in touch.",
      "duplicate": "You're already on the list!",
      "error": "Could not sign up. Try again.",
      "invalidEmail": "Enter a valid email address."
    }
  },
  "visaExplorer": {
    "pageTitle": "Visa Explorer",
    "pageSubtitle": "Pick your nationality and destination to see live visa requirements from our database.",
    "dashboardTitle": "Visa Explorer",
    "dashboardSubtitle": "Look up visa types and open a trip workspace when you are ready to plan.",
    "imFrom": "I'm from",
    "imFromHint": "Passport-issuing country (2-letter ISO code)",
    "goingTo": "Going to",
    "goingToHint": "Country or territory you plan to enter",
    "pickNationality": "Search country…",
    "pickDestination": "Search country…",
    "checkCta": "Check visa requirements",
    "checkCtaShort": "Check requirements",
    "loading": "Checking…",
    "fetchErrorFallback": "Could not load visa data. Try again.",
    "emptyStateTitle": "No visa data for this route",
    "emptyState": "We don't have data for this route yet. Check back soon.",
    "visaFreeBanner": "Good news — you don't need a visa for this trip",
    "voaBanner": "Visa on arrival — not the same as visa-free",
    "voaBannerBody": "You still need to obtain a visa at the border or airport. Fees, documents, and eligibility can apply. Confirm with the destination authority before you travel.",
    "trustVerifiedHint": "Based on human-verified route data in Wundrlst. Always confirm with the official authority before you travel.",
    "tipRelax": "Review entry rules before you travel.",
    "entryType": "Entry type",
    "entryTypeVisaFree": "Visa-free travel",
    "entryTypeVisaOnArrival": "Visa on arrival",
    "entryTypeEVisa": "E-visa",
    "entryTypeVisaRequired": "Visa required before travel",
    "detailsCta": "Details",
    "costUsd": "${{amount}} USD (estimated)",
    "costUsdEstimated": "${{amount}} USD (estimated)",
    "costUnknown": "Unknown — not confirmed",
    "durationDays": "Up to {{days}} days",
    "durationVaries": "Duration varies",
    "durationLabel": "Duration",
    "costLabel": "Cost",
    "complexityLabel": "Complexity",
    "processingTimeLabel": "Processing time",
    "complexity": {
      "low": "Low",
      "medium": "Medium",
      "high": "High"
    },
    "prEligibleShort": "Permanent residence",
    "citizenshipEligibleShort": "Citizenship",
    "yesMark": "✓",
    "noMark": "✗",
    "unknownMark": "—",
    "tabOverview": "Overview",
    "tabRequirements": "Requirements",
    "tabDocuments": "Documents",
    "aboutFallback": "Official requirements can change — always verify with the destination authority.",
    "noRequirementsListed": "No eligibility steps captured for this route yet.",
    "noDocumentsListed": "No document list captured for this route yet.",
    "ctaSignup": "Start planning this trip →",
    "ctaCreateWorkspace": "Create workspace for this trip",
    "ctaProvisioning": "Creating workspace…",
    "matchLookupLoading": "Checking your trips…",
    "hasWorkspace": "You have a workspace for this trip",
    "ctaOpenWorkspace": "Open workspace",
    "ctaCreateAnother": "Create another",
    "intentBanner": "We saved your route: {{from}} → {{to}}. You can change it below."
  }
}
